/**
 * Der deutsche Wortlaut der Oberfläche.
 *
 * **Diese Datei liest und ändert ein Mensch.** Sie trägt Wortlaut und kein
 * Warum: die Begründungen stehen weiterhin als Kommentar neben dem Code, den
 * sie erklären. Genau diese Trennung war der Anlass (#67) — die Oberfläche
 * hatte den Ton der Dokumentation geerbt, und wer einen Schalter sucht, liest
 * keinen Aufsatz.
 *
 * Drei Regeln, damit sie eine Datei zum Lesen bleibt:
 *
 * - **Ein Satz sagt, was etwas tut.** Kein Gedankenstrich, keine Begründung,
 *   keine Nominalisierung. Anrede ist „du".
 * - **Zusammengesetztes ist eine Funktion**, kein Zusammenbau im JSX. Eine
 *   andere Sprache stellt die Wörter anders.
 * - **Die Schlüssel sind Namen, nicht Text.** Ein Schlüssel, der den deutschen
 *   Satz wiederholt, ist beim ersten Umformulieren falsch.
 *
 * Seit 0.16.0 steht die ganze Oberfläche hier. In 0.28.0 wurde einmal
 * durchgelesen und geglättet: Gedankenstrich-Nachsätze, mitgelieferte
 * Begründungen und Nominalstil sind raus. Aus dem Betrieb: „klingen zum Teil
 * immer noch sehr sperrig."
 */
export const de = {
  /** Die Liste links und die Kopfzeile darüber. */
  einstellungen: {
    titel: 'Einstellungen',
    zurueck: 'Zurück',
    zurueckZuDenKategorien: 'Zurück zur Übersicht',
    konto: 'Konto',
    benachrichtigungen: 'Benachrichtigungen',
    darstellung: 'Darstellung',
    datenschutz: 'Datenschutz',
    openSource: 'Open Source',
    diagnose: 'Diagnose',
    administration: 'Administration',
    betrieb: 'Betrieb',
    /** An der Zeile mit offenen Anfragen — die Zahl steht auch im Namen. */
    offen: (titel: string, zahl: number) => `${titel}, ${zahl} offen`,
  },

  administration: {
    titel: 'Administration',
    personen: 'Personen',
    speicherplatz: 'Speicherplatz',
    medien: 'Größe von Medien',
    farben: 'Farben',
    aufenthalte: 'Aufenthalte',
    ortUndWetter: 'Ort und Wetter',
    gifs: 'GIFs',
  },

  konto: {
    darfAlles: 'Du kannst Reisen anlegen und Personen freischalten.',
    darfReisen: 'Du kannst eigene Reisen anlegen.',
    darfMitlesen: 'Du liest mit und kannst antworten.',
    abmelden: 'Abmelden',
  },

  kontokarte: {
    email: 'E-Mail-Adresse',
    emailLead: (adresse: string) =>
      `${adresse}. Damit meldest du dich an und setzt dein Passwort zurück. Ändern lässt sie sich hier nicht.`,
    name: 'Dein Name',
    nameLead: 'Dieser Name steht über deinen Nachrichten.',
    nameFeld: 'Name',
    nameSpeichern: 'Namen speichern',
    passwort: 'Passwort ändern',
    passwortLead: 'Andere Geräte werden dabei abgemeldet. Du bekommst eine E-Mail zur Bestätigung.',
    passwortAlt: 'Bisheriges Passwort',
    passwortNeu: 'Neues Passwort',
    passwortNochmal: 'Neues Passwort wiederholen',
    passwortUngleich: 'Die beiden neuen Passwörter sind nicht gleich.',
    passwortGeaendert: 'Geändert. Die anderen Geräte sind abgemeldet.',

    /**
     * Passkeys (#21). „Face ID, Touch ID oder der Code deines Geräts": das
     * sind die Wörter, die auf dem Gerät selbst stehen -- nicht „biometrisch"
     * und nicht „WebAuthn".
     */
    passkeys: 'Passkeys',
    passkeysLead: 'Anmelden mit Face ID, Touch ID oder dem Code deines Geräts, ohne Passwort. Der Passkey liegt in deinem Schlüsselbund, nicht auf dem Server.',
    passkeyAnlegen: 'Passkey hinzufügen',
    passkeyKeine: 'Noch kein Passkey.',
    passkeyNichtMoeglich: 'Dieser Browser bietet keine Passkeys an. Auf einem anderen Gerät kannst du einen anlegen.',
    /**
     * „Angelegt auf iPad" und nicht bloß „iPad": der Name ist der Browser, in
     * dem der Passkey entstand, nicht der Ort, an dem er gilt. Aus dem Betrieb:
     * am iPad angelegt, am iPhone angemeldet, es ist derselbe Schlüsselbund.
     */
    passkeyAngelegtAuf: (name: string) => `Angelegt auf ${name}`,
    passkeyAngelegtAm: (wann: string) => `am ${wann}`,
    passkeyBenutzt: (wann: string) => `zuletzt benutzt am ${wann}`,
    passkeyNieBenutzt: 'noch nie benutzt',
    passkeyEntfernen: 'Entfernen',
    passkeyEntfernenFrage: (name: string) =>
      `Den auf „${name}" angelegten Passkey entfernen? Anmelden geht damit danach nicht mehr.`,
    passkeyAbgebrochen: 'Abgebrochen. Es wurde nichts angelegt.',
    /** Der Name in der Liste, wenn sich das Gerät nicht erkennen lässt. */
    passkeyGeraet: 'Gerät',
    passkeyPasswortBleibt: 'Dein Passwort gilt weiterhin, auch mit Passkey.',
  },

  push: {
    titel: 'Benachrichtigungen',
    erstInstallieren: 'Auf dem iPhone geht das erst, wenn die App über Teilen → Zum Home-Bildschirm installiert ist. Öffne sie danach von dort.',
    kannNicht: 'Dieser Browser kann keine Benachrichtigungen empfangen.',
    an: 'Dieses Gerät bekommt eine Nachricht, wenn es etwas Neues gibt. Einzelne Reisen kannst du auf ihrer Reiseseite stummschalten.',
    aus: 'Bekomme eine Nachricht, wenn jemand etwas Neues schreibt, auch bei geschlossener App.',
    veraltet: 'Die Anmeldung dieses Geräts ist nicht mehr gültig. Schalte sie hier wieder ein.',
    verloren: 'Dieses Gerät war angemeldet, ist es aber nicht mehr. Schalte es wieder ein.',
    gesperrt: 'Der Browser blockiert Benachrichtigungen für diese Seite. Erlaube sie in den Browser-Einstellungen.',
    frageOffen: 'Der Browser wartet auf deine Antwort. Am Rechner steht die Frage oft nur als kleines Symbol links in der Adresszeile.',
    dauert: 'Das dauert ungewöhnlich lange. Lade die Seite neu und versuch es noch einmal.',
    einschalten: 'Auf diesem Gerät einschalten',
    abschalten: 'Auf diesem Gerät abschalten',
    probe: 'Testnachricht senden',
    probeEines: 'Verschickt. Die Nachricht sollte gleich ankommen.',
    probeMehrere: (zahl: number) => `An ${zahl} Geräte verschickt.`,
    symbolzahl: 'Zahl am App-Symbol',
    symbolzahlAn: 'Das App-Symbol zeigt die Zahl deiner ungelesenen Beiträge.',
    symbolzahlAus: 'Das Symbol bleibt ohne Zahl. Benachrichtigungen kommen weiter an.',
    symbolzahlNichtGemerkt: 'Das ließ sich gerade nicht speichern. Versuch es noch einmal.',
    schritte: {
      schluessel: 'Schlüssel vom Server holen …',
      erlaubnis: 'Der Browser fragt nach der Erlaubnis …',
      worker: 'Hintergrunddienst anmelden …',
      abonnement: 'Abonnement beim Push-Dienst lösen …',
      server: 'Gerät beim Server eintragen …',
    },
  },

  installieren: {
    titel: 'Auf dem iPhone installieren',
    lead: 'Tippe in Safari unten auf Teilen und dann auf „Zum Home-Bildschirm". Nur die installierte App kann dich benachrichtigen.',
  },

  schrift: {
    titel: 'Schriftgröße',
    lead: 'Gilt in der ganzen App und auf allen deinen Geräten. Im Chat passt sich die Größe zusätzlich dem Bildschirm an.',
    wahl: 'Schriftgröße',
    nichtGemerkt: 'Die Größe gilt auf diesem Gerät. Für deine anderen Geräte ließ sie sich nicht speichern.',
  },

  erkennung: {
    titel: 'Aufenthalte',
    lead: 'Beim Import einer Route sucht die App nach längeren Stopps und schlägt sie als Highlights vor.',
    minuten: 'Mindestdauer (Minuten)',
    minutenHinweis: '0 schaltet die Erkennung ab.',
    umkreis: 'Umkreis (Meter)',
    umkreisHinweis: '800 m sind ein guter Wert. Bei mehr verschmelzen zwei Stopps in einer Stadt zu einem, und eine langsame Durchfahrt zählt als Aufenthalt.',
  },

  gif: {
    titel: 'GIFs',
    lead: 'Mitlesende können mit einem GIF antworten statt mit Worten.',
    name: 'GIF-Auswahl',
    an: 'Der GIF-Knopf steht in der Eingabezeile. Die Suche läuft über den Server, zu Giphy geht nur der Suchbegriff. Ein gewähltes GIF speichert der Server wie ein hochgeladenes Bild und zählt es zum Speicherplatz der Reise.',
    aus: 'Ohne Schlüssel gibt es keinen GIF-Knopf, alles andere läuft. Einen kostenlosen Schlüssel bekommst du auf developers.giphy.com („Create an App", Typ API). Er kommt in die .env auf dem Server:',
  },

  kontoloeschung: {
    oeffnen: 'Konto löschen …',
    wirdGezaehlt: 'Wird gezählt …',
    uebersichtFehlt: 'Die Übersicht lässt sich gerade nicht laden.',
    verschwindet: 'Das verschwindet:',
    geschrieben: (zahl: number) =>
      zahl === 1 ? '1 geschriebene Nachricht' : `${zahl} geschriebene Nachrichten`,
    erhalten: (zahl: number) =>
      zahl === 1
        ? '1 Nachricht an diese Person, auch vom Reisenden geschriebene'
        : `${zahl} Nachrichten an diese Person, auch vom Reisenden geschriebene`,
    dateien: (zahl: number) => (zahl === 1 ? '1 Datei' : `${zahl} Dateien`),
    reaktionen: (zahl: number) => (zahl === 1 ? '1 Reaktion' : `${zahl} Reaktionen`),
    geraete: (zahl: number) =>
      zahl === 1 ? '1 angemeldetes Gerät' : `${zahl} angemeldete Geräte`,
    betroffeneReisen: 'Betroffene Reisen:',
    reiseZeile: (titel: string, zahl: number) =>
      `${titel}: ${zahl === 1 ? '1 Nachricht' : `${zahl} Nachrichten`}`,
    gesperrt: (reisen: string) =>
      `Diesem Konto gehören noch Reisen: ${reisen}. Lösche erst die Reisen, dann das Konto. Bis dahin kannst du es nur stilllegen.`,
    endgueltig: 'Das lässt sich nicht rückgängig machen. Alles, was diese Person geschrieben hat, verschwindet aus allen Reiseberichten.',
    abtippen: 'Zum Bestätigen abtippen:',
    abtippenName: (adresse: string) => `E-Mail-Adresse ${adresse} abtippen`,
    ohneNetz: 'Ohne Netz geht das gerade nicht.',
    abbrechen: 'Abbrechen',
    loeschen: 'Endgültig löschen',
    wirdGeloescht: 'Wird gelöscht …',
    geloescht: (name: string, nachrichten: number, dateien: number) =>
      `„${name}" ist gelöscht: ${nachrichten === 1 ? '1 Nachricht' : `${nachrichten} Nachrichten`} und ${dateien === 1 ? '1 Datei' : `${dateien} Dateien`} sind weg.`,
  },

  personen: {
    lead: 'Wer über einen Einladungslink kommt, liest mit und antwortet. Eigene Reiseberichte schreiben darf nur, wen du dafür freischaltest. Den Namen für die Verwaltung siehst nur du.',
    wirdGeladen: 'Wird geladen …',
    du: ' · du',
    stillgelegt: ' · stillgelegt',
    reisen: (zahl: number) => (zahl === 1 ? '1 Reise' : `${zahl} Reisen`),
    nenntSichSelbst: (name: string) => `nennt sich selbst ${name}`,
    namensfeld: (name: string) => `Name für die Verwaltung, sonst ${name}`,
    rolleVon: (name: string) => `Rolle von ${name}`,
    eigeneRolle: 'Deine eigene Rolle kann nur eine zweite Verwaltung ändern.',
    liestMit: 'Liest mit',
    darfReisen: 'Darf Reisen anlegen',
    darfVerwalten: 'Darf Reisen anlegen und verwalten',
    stilllegen: 'Konto stilllegen',
    stilllegenKurz: 'Stilllegen',
    stilllegenFrage: (name: string) =>
      `${name} stilllegen? Die Person wird sofort abgemeldet und kann sich nicht mehr anmelden. Reisen, Nachrichten und Mitgliedschaften bleiben. Du kannst das jederzeit aufheben.`,
    freigeben: 'Wieder freigeben',
    supervisor: 'Supervisor',
    supervisorHinweis: 'Ein Supervisor sieht und ändert die technischen Einstellungen unter Betrieb.',
    supervisorSelbst: 'Dein eigenes Merkmal kann nur ein zweiter Supervisor ändern.',
    supervisorMarke: ' · Supervisor',
  },

  /** Die Kategorie „Betrieb" für den Supervisor (#62). */
  betrieb: {
    titel: 'Betrieb',
    konfiguration: 'Konfiguration',
    selbstpruefung: 'Selbstprüfung',
    protokoll: 'Protokoll',
    konfigurationLead: 'Die Werte der Datei .env auf dem Server. Der Server liest sie bei jeder Anfrage neu, eine Änderung gilt sofort.',
    nichtSchreibbar: 'Die Datei .env ist für den Server nicht beschreibbar. Ändere sie per FTP.',
    fehlt: 'Auf dem Server liegt keine Datei .env.',
    zurueckNehmen: 'Letzte Änderung zurücknehmen',
    zurueckgenommen: 'Zurückgenommen.',
    unbekannt: 'Zeilen in der Datei, die die Anwendung nicht kennt:',
    nurFtp: 'Nur per FTP.',
    ausUmgebung: 'Kommt aus einer Umgebungsvariable des Servers. Die Datei ändert daran nichts.',
    quelleDatei: 'aus der Datei',
    quelleVorgabe: 'Vorgabe',
    gesetzt: 'gesetzt',
    leer: 'leer',
    neuerWert: 'Neuen Wert eingeben',
    speichern: 'Speichern',
    gespeichert: 'Gespeichert.',
    auskommentieren: 'Aus',
    vorgabe: (wert: string) => `Vorgabe: ${wert}`,
    decke: (wert: number | null | undefined, gewaehlt: number | null | undefined) =>
      `Decke für die Grenzen. Eingestellt: ${gewaehlt ?? '–'}, höchstens ${wert ?? '–'}.`,
    geprueft: 'Ein falscher Wert hier sperrt aus oder erreicht jeden Mitlesenden. Bitte genau prüfen.',
    geprueftFrage: (key: string) => `${key} wirklich ändern? Der Server prüft die Form des Werts, aber nicht, ob er zu deiner Installation passt.`,
    aendern: 'Ändern',
    selbstpruefungLead: 'Was das Hosting entscheidet und was die Anwendung darüber weiß.',
    version: 'Version',
    build: 'Commit',
    datenbank: 'Datenbank',
    schema: 'Schema vollständig',
    schemaFehlt: (spalten: string[]) => `Es fehlt: ${spalten.join(', ')}`,
    php: 'PHP',
    envDatei: '.env vorhanden',
    envSchreibbar: '.env beschreibbar',
    medienOrdner: 'Medienordner beschreibbar',
    postMax: 'post_max_size',
    uploadMax: 'upload_max_filesize',
    speicherGrenze: 'memory_limit',
    laufzeit: 'max_execution_time',
    zip: 'ZIP (für EPUB und Medien)',
    gd: 'GD (Bilder)',
    openssl: 'OpenSSL (Passkeys, Apple)',
    curl: 'cURL (Push, Apple, Giphy)',
    buchSchrift: 'Schrift für das Buch',
    buchEmoji: 'Emoji-Bilder für das Buch',
    kartenquelleFest: 'Feste Kachelquelle',
    push: 'Push eingerichtet',
    appleMaps: 'Apple Maps',
    appleWeather: 'Apple Weather',
    giphy: 'Giphy',
    mail: 'E-Mail',
    mailWie: (transport: string, absender: boolean) => `${transport}${absender ? '' : ', ohne Absender'}`,
    betreiber: 'Betreiberangaben vollständig',
    appUrl: 'APP_URL gesetzt',
    protokollLead: 'Wer wann welchen Wert geändert hat. Bei Geheimnissen steht nur, dass sie geändert wurden.',
    protokollLeer: 'Noch keine Änderung.',
    protokollZeile: (wer: string, wann: string) => `${wer} · ${wann}`,
    protokollAlt: (alt: string | null, neu: string | null) => `${alt ?? '–'} → ${neu ?? '–'}`,
    protokollGeheim: 'Wert nicht aufgezeichnet',
    protokollUndo: ' · zurückgenommen',
    unbekanntePerson: 'unbekannt',
  },

  medien: {
    titel: 'Medien prüfen',
    lead: 'Neu hochgeladene Videos spielen auf allen Geräten ab. Ältere Videos können in einem Format vorliegen, das nicht jedes Gerät abspielt.',
    pruefen: 'Bestand prüfen',
    wirdGeprueft: 'Wird geprüft …',
    pruefungFehlt: 'Die Prüfung hat nicht geklappt.',
    allesGut: (dateien: string) => `${dateien} geprüft, alle spielen überall ab.`,
    befunde: (dateien: string, zahl: number) =>
      `${dateien} geprüft, ${zahl} davon spielen nicht überall ab.`,
    offen: (zahl: number) =>
      zahl === 1 ? ` 1 weitere steht noch aus, sie kommt beim nächsten Lauf.` : ` ${zahl} weitere stehen noch aus, sie kommen beim nächsten Lauf.`,
    abgleichTitel: 'Dateien und Einträge',
    abgleichLead: 'Findet Dateien ohne Eintrag in der Datenbank und Einträge ohne Datei. Beides verfälscht den Speicherplatz.',
    abgleichen: 'Dateien abgleichen',
    wirdAbgeglichen: 'Wird abgeglichen …',
    abgleichFehlt: 'Der Abgleich hat nicht geklappt.',
    ohneEintrag: (dateien: string) => `${dateien} ohne Eintrag`,
    ohneDatei: (eintraege: string) => `${eintraege} ohne Datei`,
    angesehen: (dateien: string) => `${dateien} angesehen`,
    stehenAus: (zahl: number) => `, ${zahl} stehen noch aus`,
    abgleichFussnote: 'Dateien ohne Eintrag werden im ganzen Speicher gesucht, Einträge ohne Datei nur in deinen Reisen. Dateien vom letzten Tag bleiben außen vor, sie können noch im Upload sein.',
    dateiOhneEintrag: 'Datei ohne Eintrag',
    eintragOhneDatei: 'Eintrag ohne Datei',
    reisebildFehlt: 'Bild der Reise fehlt, die Reise bleibt',
    standbildFehlt: 'Standbild fehlt, das Video spielt weiter',
    beitrag: (id: number) => ` · Beitrag ${id}`,
    punkt: (id: number) => ` · Punkt ${id}`,
    dateiLoeschen: 'Datei löschen',
    dateiLoeschenFrage: 'Diese Datei löschen? Sie hängt an keinem Beitrag und keiner Reise. Das lässt sich nicht rückgängig machen.',
    eintraegeEntfernen: (eintraege: string) => `${eintraege} entfernen`,
    eintraegeEntfernenFrage: (eintraege: string) =>
      `${eintraege} ohne Datei entfernen? Die Beiträge bleiben stehen und verlieren nur den Anhang. Das lässt sich nicht rückgängig machen.`,
    wirdEntfernt: 'Wird entfernt …',
    datei: 'Datei',
    dateien: 'Dateien',
    eintrag: 'Eintrag',
    eintraege: 'Einträge',
  },

  speicher: {
    titel: 'Speicherplatz',
    lead: 'Fotos, Videos und Sprachnachrichten aller Reisen zusammen. Ist der Platz voll, nimmt die App nichts Neues mehr an.',
    wirdGezaehlt: 'Wird gezählt …',
    nichtErmittelt: 'Das ließ sich nicht ermitteln.',
    belegt: (prozent: number) => `${prozent} Prozent belegt`,
    fotos: 'Fotos',
    videos: 'Videos',
    sprache: 'Sprache',
    zurueckgezogen: 'zurückgezogen',
    abgeschlossen: 'abgeschlossen',
    alleVorschaubilder: 'Alle Fotos haben ein Vorschaubild.',
    vorschaubilder: (mit: number, gesamt: number) =>
      `Vorschaubilder: ${mit} von ${gesamt} Fotos. Ältere Fotos haben keins und zeigen in der Kachel das Original.`,
    reisenAnderer: (groesse: string) => `Reisen anderer: ${groesse}`,
    keineBuecher: 'Es liegt kein gebautes Buch auf dem Server.',
    buecher: (zahl: number, groesse: string) =>
      zahl === 1
        ? `1 gebautes Buch (${groesse}) liegt auf dem Server. Sein Link gilt zwei Stunden, danach räumt der nächste Buchbau, Abruf oder dieser Knopf es weg.`
        : `${zahl} gebaute Bücher (${groesse}) liegen auf dem Server. Ein Link gilt zwei Stunden, danach räumt der nächste Buchbau, Abruf oder dieser Knopf sie weg.`,
    buecherWegraeumenFrage: 'Gebaute Bücher löschen, deren Link abgelaufen ist? Ein Buch lässt sich jederzeit neu bauen.',
    nichtsLiegt: 'In deinen Reisen liegt nichts herum.',
    liegt: (zahl: number, groesse: string) =>
      zahl === 1
        ? `1 liegengebliebene Datei (${groesse}) hängt an keiner Nachricht und an keinem Punkt.`
        : `${zahl} liegengebliebene Dateien (${groesse}) hängen an keiner Nachricht und an keinem Punkt.`,
    weitere: (zahl: number) => `… und ${zahl} weitere. Der Knopf räumt alle weg.`,
    nichtsWegzuraeumen: 'Es war nichts wegzuräumen.',
    weggeraeumt: (zahl: number, groesse: string) =>
      zahl === 1 ? `1 Datei weggeräumt, ${groesse} frei.` : `${zahl} Dateien weggeräumt, ${groesse} frei.`,
    wegraeumen: 'Liegengebliebenes wegräumen',
    wegraeumenKurz: 'Wegräumen',
    wegraeumenFrage: (zahl: number) =>
      `${zahl} Dateien löschen, die an keiner Nachricht und keinem Punkt hängen? Es sind Reste abgebrochener Uploads. Das lässt sich nicht rückgängig machen.`,
    heute: 'heute',
    seitEinemTag: 'seit einem Tag',
    seitTagen: (tage: number) => `seit ${tage} Tagen`,
    vonMitlesendem: 'von einem Mitlesenden',
    artFoto: 'Foto',
    artGif: 'GIF',
    artVideo: 'Video',
    artSprache: 'Sprachnachricht',
  },

  grenzen: {
    titel: 'Fotos und Videos',
    lead: 'Gilt für alle Reisen. Die Bildgröße wirkt ab dem nächsten Foto, die Grenzen sofort.',
    fotoKante: 'Lange Kante der Fotos',
    fotoKanteHinweis: 'Kleiner wird körnig, größer kostet viel Platz für wenig Gewinn.',
    fotoQualitaet: 'Bildqualität',
    fotoQualitaetHinweis: 'Unter 60 sieht man es, über 90 kostet viel Platz für wenig.',
    leserKante: 'Lange Kante bei Fotos von Mitlesenden',
    leserKanteHinweis: 'Gilt für alle Mitlesenden in allen Reisen. Fotos in Antworten dürfen kleiner sein als die im Reisebericht.',
    leserQualitaet: 'Bildqualität bei Fotos von Mitlesenden',
    fotoMax: 'Größtes Foto',
    videoKante: 'Lange Kante der Videos',
    videoKanteHinweis: '1920 entspricht 1080p. Die Grenze gilt für die lange Kante, quer wie hochkant. Verkleinert wird auf dem Gerät des Absenders.',
    videoFps: 'Bilder je Sekunde',
    videoFpsHinweis: 'Ein iPhone nimmt mit 60 auf, Zeitlupe mit 240. Eine Zeitlupe bleibt eine Zeitlupe, sie wird nur etwas ruckeliger.',
    videoMax: 'Größtes Video',
    videoMaxHinweis: 'Die Grenze gilt nach dem Verkleinern und fängt Videos ab, die ein Gerät nicht umrechnen konnte.',
    rechnen: 'Rechnen beim Verkleinern',
    rechnenQualitaet: 'Qualität (höhere Datenrate)',
    rechnenTempo: 'Tempo (kleinere Datei)',
    rechnenHinweis: '„Tempo" ergibt kleinere Dateien mit gröberem Bild, „Qualität" größere mit feinerem.',
    platzJeReise: 'Platz je Reise',
    deckel: 'Die Obergrenzen stehen in der .env auf dem Server. Höhere Werte werden auf diese Grenzen gesetzt.',
    feld: (label: string, einheit: string) => `${label} (${einheit})`,
    hoechstens: (decke: number) => `Höchstens ${decke} laut .env.`,
  },

  dienste: {
    titel: 'Ort und Wetter',
    lead: 'Ortsname und Wetter zu jedem Punkt auf der Karte, zur Stunde, in der du dort warst.',
    ortsnamen: 'Ortsnamen',
    wetter: 'Wetter',
    schluesselFehlt: 'Die Schlüsseldatei fehlt: Unter APPLE_PRIVATE_KEY_PATH in der .env liegt keine Datei, die der Server lesen kann. Prüf Pfad und Leserechte. Ein relativer Pfad zählt ab dem Verzeichnis der index.php. Bis dahin gibt es keine Ortsnamen und kein Wetter.',
    aus: 'Nicht eingerichtet. Vorschläge aus der Route heißen dann „Aufenthalt, 2 Std." statt nach ihrem Ort. Wie du die Dienste einrichtest, steht in der Installationsanleitung.',
    halb: 'Eines von beiden fehlt. Prüf die .env auf dem Server:',
    an: 'Beides läuft. Der Server holt die Angaben einmal je Highlight. „In der Nähe finden" nutzt denselben Zugang auf Knopfdruck.',
    umkreis: 'Umkreis der Umgebungssuche (Meter)',
    umkreisHinweis: 'So weit sucht „In der Nähe finden". 500 m sind Gehweite. Mehr bringt mehr Treffer, aber auch solche, an denen niemand war.',
    weitUmkreis: 'Umkreis für weitere Orte (Meter)',
    weitUmkreisHinweis: 'Für die Spots, die unter einem Punkt aufgelistet werden. Er darf größer sein als der obere.',
    weitZuKlein: 'Zurzeit ist er kleiner als der obere.',
    eingerichtet: 'eingerichtet',
    buchEmoji: 'Emoji-Bilder für das Buch',
    buchEmojiFehlt: 'Die Datei fonts/noto-emoji-72.zip fehlt auf dem Server oder ist nicht lesbar. Im PDF und EPUB fehlen dann alle Emojis. Sie liegt im Bündel unter fonts/.',
    nichtEingerichtet: 'nicht eingerichtet',
  },

  farben: {
    listeEigene: 'Farben der Reiseübersicht',
    listeEigeneLead: 'Gilt nur für dich, auf allen deinen Geräten. Eigene Reisen tragen zusätzlich einen Streifen am linken Rand.',
    listeVorgabe: 'Farben der Reiseübersicht',
    listeVorgabeLead: 'Die Vorgabe für alle. Wer sich selbst etwas anderes einstellt, sieht seine eigene Wahl.',
    voreinstellung: 'Voreinstellung',
    allesVoreinstellung: 'Alles auf Voreinstellung',
    /**
     * Die zwei Zeilen der Vorschau.
     *
     * Sie standen als deutscher Text im JSX -- ein Rest, den der Prüflauf
     * `texte.ts` nicht gesehen hat, weil er in einem Textknoten über mehrere
     * Zeilen steht. Beim Anfassen mitgenommen.
     */
    listeBeispielEigen: 'Deine eigene Reise',
    listeBeispielFremd: 'Eine Reise, bei der du mitliest',
    automatisch: 'Automatisch',
    allesAutomatisch: 'Alles automatisch',
    blasen: 'Farbe der Sprechblasen',
    blasenLead: 'Die Vorgabe für neue Reisen. Bei einer bestehenden Reise steht die Farbe auf ihrer Infoseite unter „Aussehen".',
    /** Die Voreinstellung für die Karte einer neuen Reise (#50). */
    karte: {
      titel: 'Karte',
      lead: 'Die Vorgabe für neue Reisen: Kartenbild, Farbe und Stärke der Route, Farben der Nadeln. Eine bestehende Reise stellt der Reisende auf ihrer Karte ein, unter „Bearbeiten".',
      kartenbild: 'Kartenbild',
      fest: 'In der .env ist eine feste Quelle eingetragen (MAP_TILE_URL). Sie gilt für alle Reisen.',
      routenfarbe: 'Farbe der Route',
      strichstaerke: 'Stärke der Route',
      nadeln: 'Nadeln und Kärtchen',
      vorgabe: 'Alles auf Vorgabe',
    },
    knoepfe: 'Farbe der Knöpfe',
    knoepfeLead: 'Gilt für alle, in der ganzen App: Knöpfe, Sendeknopf und alles andere in der Akzentfarbe.',
    knoepfeAutomatisch: 'Automatisch heißt: hell und dunkel bekommen je einen eigenen Ton.',
    /**
     * Die Namen der einzelnen Farbfelder.
     *
     * Sie standen in `lib/blasenfarben.ts` und `lib/listenfarben.ts` neben
     * den CSS-Variablen. Die Mechanik bleibt dort, die Wörter kommen hierher.
     */
    blasenFelder: {
      chat_own_color: { titel: 'Deine Beiträge', hinweis: 'Rechts, und beim Leser seine eigenen.' },
      chat_other_color: {
        titel: 'Beiträge der anderen',
        hinweis: 'Links, also die Antworten deiner Leser.',
      },
    },
    listenFelder: {
      own: { titel: 'Eigene Reisen', hinweis: 'Berichte, die du selbst schreibst.' },
      other: { titel: 'Reisen anderer', hinweis: 'Berichte, bei denen du mitliest.' },
      bg: { titel: 'Freie Fläche', hinweis: 'Der Platz unter der Liste.' },
    },
  },

  /** Die Karten der Diagnose. Messwerte bleiben Messwerte. */
  verbindung: {
    titel: 'Verbindung',
    online: 'Die letzte Anfrage hat den Server erreicht. Was du schreibst, geht sofort raus.',
    offline: 'Der Server ist gerade nicht erreichbar. Was du schreibst, wartet auf dem Gerät.',
    erreicht: 'Zuletzt erreicht',
    browserMeldet: 'Der Browser meldet',
    wartende: 'Wartende Nachrichten',
    ja: 'ja',
    nein: 'nein',
    taubesNetz: 'Das Gerät hängt an einem Netz ohne Verbindung nach draußen, zum Beispiel einem WLAN ohne Internet. Das passiert auch im Flugmodus mit eingeschaltetem WLAN.',
    unverschluesselt: (protokoll: string) =>
      `Diese Seite läuft unverschlüsselt (${protokoll}). Benachrichtigungen gehen so nicht, und jeder im selben Netz kann mitlesen. Ruf sie über https:// auf. Ein Symbol auf dem Home-Bildschirm legst du von der sicheren Adresse aus neu an.`,
  },

  tastatur: {
    titel: 'Tastatur',
    lead: 'Tippe ins Feld. Bleibt es über der Tastatur sichtbar, sitzt es richtig.',
    feld: 'Hier tippen',
    feldName: 'Testfeld für die Tastatur',
    hoeheApp: 'Höhe der App',
    vonFenster: (fenster: number | string) => `von ${fenster}`,
    abstand: 'Freigehaltener Abstand',
    fensterhoehe: 'Fensterhöhe',
    sichtbareHoehe: 'Sichtbare Höhe',
    versatz: 'Versatz der Fläche',
    seitenversatz: 'Seitenversatz',
    gesetzteHoehe: 'Gesetzte Höhe',
    nichtGesetzt: 'nicht gesetzt',
    greiftNicht: 'greift nicht',
    dvh: '100dvh ergibt',
    bildschirm: 'Bildschirm',
    fensterbreite: 'Fensterbreite',
    alsApp: 'Als App',
    mindesthoehe: 'Mindesthöhe vom Bildschirm',
    raender: 'Sicherheitsabstände',
    mitschrieb: 'Mitschrieb: Fenster / sichtbar / gesetzt / App / Unterkante · Abstand · Seite ↕ Fläche, neueste zuerst:',
    senden: 'Senden und Videos, neueste zuerst:',
    sendenLeer: 'Noch nichts. Schreib im Chat etwas und tippe auf Senden, oder häng ein Video an, dann komm zurück.',
  },

  spruenge: {
    titel: 'Sprünge aus Benachrichtigungen',
    lead: 'Was beim letzten Tipp auf eine Benachrichtigung passiert ist. Oben der Hintergrunddienst, unten die App. Diese Daten bleiben auf dem Gerät.',
    oberflaeche: 'Oberfläche',
    imHintergrund: 'Im Hintergrund',
    inDerApp: 'In der App',
    wirdGelesen: 'Wird gelesen …',
    keinWorker: 'Auf diesem Gerät läuft kein Hintergrunddienst. Ohne ihn gibt es keine Benachrichtigungen.',
    keineAntwort: 'Der Hintergrunddienst antwortet nicht. Ob er etwas aufgezeichnet hat, bleibt offen.',
    nichts: 'Der Hintergrunddienst hat nichts aufgezeichnet: Seit dem letzten Aufräumen kam keine Benachrichtigung an.',
    nichtsInDerApp: 'Seit dem Start ist hier nichts angekommen.',
    neuLesen: 'Neu lesen',
    /**
     * Die Zeile, die „nicht getippt" von „getippt, kam nicht an" trennt.
     *
     * Eine App kommt nicht von selbst nach vorn. Steht hier ein Zeitpunkt
     * wenige Sekunden nach einer Push-Nachricht und oben kein „Angetippt", dann
     * hat jemand getippt und der Service Worker hat es nicht erfahren.
     */
    zuletztVorn: (zeit: string) => `Zuletzt nach vorn gekommen: ${zeit}`,
    nieVorn: 'Die App war noch nicht im Hintergrund.',
  },

  stand: {
    /**
     * **Die Karte steht ganz oben in der Diagnose** — aus dem Betrieb: „die
     * Infos sind oft wichtiger als die von weiter unten". Sie beantwortet die
     * Frage vor allen anderen: welche Fassung läuft hier?
     */
    titel: 'Stand',
    lead: 'Welche Fassung dieses Gerät gerade fährt und welche der Server. Das Erste, was bei einer Meldung gefragt wird.',
    zeile: (version: string, oberflaeche: string, server: string) =>
      `Travel Broadcast ${version} · Oberfläche ${oberflaeche} · Server ${server}`,
    anzeige: (art: string, herkunft: string) => `Anzeige: ${art} · ${herkunft}`,
    gelesen: (ziel: string) => `beim Öffnen gelesen: ${ziel}`,
    gemerkt: (ziel: string) => `jetzt gemerkt: ${ziel}`,
    uebersicht: 'Übersicht',
    keinZiel: 'nichts',
    chat: (pfad: string) => `Chat ${pfad}`,
    app: 'App',
    browser: 'Browser',
    browserOhneLeisten: 'Browser (ohne Leisten)',
    unsicher: 'unsicher',
  },

  /**
   * Der Chat — Verlauf, Eingabezeile und was daran hängt.
   *
   * **Der Wortlaut ist beim Umzug Zeichen für Zeichen derselbe geblieben.**
   * Gemeckert war über die Einstellungen (#67), und die sind erledigt; ob
   * hier etwas sperrig klingt, entscheidet sich besser an dieser Datei als
   * beim Verschieben.
   */
  chat: {
    // --- Auswählen und löschen ---------------------------------------------
    auswahlLeiste: 'Nachrichten löschen',
    nichtsAusgewaehlt: 'Nichts ausgewählt',
    nachrichten: (anzahl: number) => (anzahl === 1 ? '1 Nachricht' : `${anzahl} Nachrichten`),
    ausgewaehlt: (nachrichten: string) => `${nachrichten} ausgewählt`,
    loeschenName: (nachrichten: string) => `${nachrichten} löschen`,
    ohneNetz: 'Ohne Netz geht das gerade nicht.',

    // --- Antworten der Mitlesenden -----------------------------------------
    antworten: 'Antworten',
    antwortenName: 'Antworten der Mitlesenden',
    antwortenUngelesen: (ungelesen: number) =>
      `Antworten der Mitlesenden, ${ungelesen} ungelesen`,

    // --- Was im Ausgangskorb wartet ----------------------------------------
    wartet: '⏳ wartet auf Netz',
    nichtAngenommen: '⚠︎ nicht angenommen',
    nochmal: 'Nochmal versuchen',
    wartendeVerwerfen: 'Diese wartende Nachricht verwerfen?',

    // --- Abstimmungen ------------------------------------------------------
    abstimmung: 'Abstimmung',
    /** Die Wetterkarte (0.31.0): ein Eintrag im Plus-Menü und drei Stände dahinter. */
    wetter: 'Aktuelles Wetter',
    wetterOrtung: 'Standort wird ermittelt …',
    wetterHolen: 'Das Wetter wird geholt …',
    wetterOhneNetz: 'Ohne Netz lässt sich das Wetter nicht holen.',
    /** Der letzte Tag (#79): ein Eintrag im Plus-Menü und drei Stände dahinter. */
    streckenstatistik: 'Streckenstatistik',
    tagAuswerten: 'Die Route wird ausgewertet …',
    tagZeichnen: 'Die Karte wird gezeichnet …',
    tagSchicken: 'Die Karte wird gesendet …',
    tagOhneNetz: 'Ohne Netz lässt sich der Tag nicht auswerten.',
    tagWelcher: 'Welcher Tag?',
    tagFeld: 'Tag der Route',
    tagVon: 'Von',
    tagBis: 'Bis',
    tagTeilHinweis: 'Vorbelegt mit der ersten und letzten Bewegung des Tages. Wer nur einen Teil will, ändert die Zeiten.',
    tagSenden: 'Karte senden',
    abstimmungAnlegen: 'Abstimmung anlegen',
    frage: 'Frage',
    fragePlatzhalter: 'Wohin morgen?',
    antwortenFeld: 'Antworten',
    antwortPlatzhalter: (i: number) => (i === 0 ? 'Berge' : i === 1 ? 'Meer' : 'Noch etwas'),
    mehrfachErlauben: 'Mehrfachauswahl erlauben',
    abstimmungZusage:
      'Alle in dieser Reise können abstimmen. Sie sehen danach nur Prozente, keine Stimmenzahl und keine Namen.',
    abstimmungZusageBetont: 'Wer wie abgestimmt hat, siehst nur du.',
    abstimmungSenden: 'Abstimmung senden',
    stimmen: (anzahl: number) => (anzahl === 1 ? 'Stimme' : 'Stimmen'),
    ergebnis: 'Ergebnis',
    ergebnisZahl: (anzahl: number) => `Ergebnis · ${anzahl} ${anzahl === 1 ? 'Stimme' : 'Stimmen'}`,
    stimmenZeile: (anzahl: number, offen: boolean) =>
      `${anzahl} ${anzahl === 1 ? 'Stimme' : 'Stimmen'} · `
      + (offen ? 'noch offen, Stimmen können sich ändern' : 'geschlossen'),
    werWie: 'Wer wie',
    niemandAbgestimmt: 'Noch hat niemand abgestimmt.',
    geschlossen: 'Geschlossen',
    mehrfach: 'Mehrfachauswahl',
    abstimmungSchliessen:
      'Abstimmung schließen? Danach kann niemand mehr abstimmen. Das Ergebnis bleibt stehen.',
    nochNichtAbgestimmt: (mehrfach: boolean) =>
      (mehrfach ? 'Kreuze an, was passt' : 'Tipp auf eine Antwort')
      + '. Danach siehst du, wie die anderen abgestimmt haben.',
    nurAnteile: 'Anteile der Abstimmenden. Wer wie abgestimmt hat, sieht nur der Reisende.',

    // --- Was sich anhängen lässt -------------------------------------------
    wasAnhaengen: 'Was anhängen?',
    fotoOderVideo: 'Foto oder Video',
    nurFoto: 'Foto',

    // --- Die Eingabezeile ------------------------------------------------------
    chatGeschlossen: 'Der Chat dieser Reise ist geschlossen.',
    bezugEntfernen: 'Bezug entfernen',
    etwasAnhaengen: 'Etwas anhängen',
    fotosHinzufuegen: 'Fotos hinzufügen',
    fotosUndVideosHinzufuegen: 'Fotos und Videos hinzufügen',
    fotoEntfernen: 'Foto wieder entfernen',
    entfernen: 'Entfernen',
    wartetAufNetz: 'Wartet auf Netz',
    nurVideosAbgewiesen: 'In einer Antwort kannst du Fotos und GIFs schicken, keine Videos.',
    videosAbgewiesen: (anzahl: number) =>
      `${anzahl === 1 ? 'Ein Video bleibt' : `${anzahl} Videos bleiben`} außen vor. `
      + 'In einer Antwort kannst du Fotos und GIFs schicken, keine Videos.',
    videoNichtDurch: 'Das Video ging nicht durch.',
    wederBildNochVideo: 'Das ist weder ein Bild noch ein Video.',
    bildNichtDurch: 'Das Bild ging nicht durch.',
    bildNichtGeoeffnet: 'Das Bild ließ sich nicht öffnen.',
    mikrofonGesperrt:
      'Das Mikrofon ist nicht freigegeben. Erlaube es in den Einstellungen des Browsers.',
    formatStumm:
      'Dein Browser nimmt in einem Format auf, das Safari nicht abspielt, und das Umwandeln hat nicht geklappt. Auf einem iPhone bleibt diese Aufnahme stumm.',
    aufnahmeNichtDurch: 'Die Aufnahme ging nicht durch.',
    videoBrauchtNetz:
      'Ein Video braucht Netz. Nimm es heraus, dann gehen Text und Fotos später von selbst raus.',
    fotoKamNichtDurch: 'Ein Foto kam nicht durch. Prüf es und schick es noch einmal ab.',
    sendetSpaeter: 'Wird gesendet, sobald alles durch ist …',
    fertigVon: (fertig: number, alle: number) => `${fertig} von ${alle} fertig …`,
    nachrichtNichtRaus: 'Die Nachricht ging nicht raus.',
    videoWirdVerkleinert: 'Video wird verkleinert …',
    kompatibelHinweis: 'Wähle beim Teilen auf dem iPhone',
    kompatibelBetont: '„Am kompatibelsten"',
    kompatibelRest: ', damit sie auch am Rechner und auf Android laufen.',
    grenzeLiegtBei: (mb: number) => `Die Grenze liegt bei ${mb} MB.`,
    ueberGrenze: (mb: number) => `über ${mb} MB`,

    // --- Die Galerie und die Großansicht --------------------------------------
    fotoVergroessern: (nummer: number, alle: number) => `Foto ${nummer} von ${alle} vergrößern`,
    grossansicht: 'Foto in Großansicht',
    vorherigesFoto: 'Vorheriges Foto',
    naechstesFoto: 'Nächstes Foto',
    fotoZaehler: (nummer: number, alle: number) => `${nummer} von ${alle}`,
    fotoSichern: 'Foto sichern',
    fotoGesichert: 'Im Ordner „Downloads" gespeichert. Dort findet es auch die Galerie.',
    fotoNichtGeholt: 'Das Foto ließ sich nicht laden. Dafür brauchst du Netz.',
    aufFotoAntworten: 'Auf dieses Foto antworten',

    // --- GIFs ---------------------------------------------------------------
    gifWahl: 'GIF auswählen',
    gifSuchen: 'GIFs suchen',
    gifPlatzhalter: 'GIFs suchen …',
    gifSchliessen: 'Auswahl schließen',
    gifNichts: 'Dazu gibt es kein GIF.',
    gifOhneNetz: 'Ohne Netz gibt es keine GIFs.',
    gifSucheFehlt: 'Die Suche hat gerade nicht geklappt.',
    gifNichtGeholt: 'Das GIF ließ sich nicht holen.',
    gifSuchtGerade: 'Suche …',

    // --- Reaktionen ----------------------------------------------------------
    reagieren: 'Reagieren',
    reagierenName: 'Mit einem Emoji reagieren',
    mehrEmoji: 'Mehr Emoji',

    // --- Sprachnachricht aufnehmen -------------------------------------------
    aufnahme: 'Sprachnachricht aufnehmen',
    aufnahmeVerwerfen: 'Aufnahme verwerfen',
    aufnahmeAnhalten: 'Aufnahme anhalten',
    aufnahmeWeiter: 'Weiter aufnehmen',
    pause: 'Pause',
    weiter: 'Weiter',
    aufnahmeSenden: 'Sprachnachricht senden',
    senden: 'Senden',

    blasenmenue: 'Was mit dieser Nachricht?',
    punktWeg: 'nicht mehr auf der Karte',
    wetterNennung: 'Wetter:',
    weiterePunkte: (anzahl: number) =>
      (anzahl === 1 ? 'und ein weiterer Punkt' : `und ${anzahl} weitere Punkte`)
      + ' auf der Karte',

    // --- Der Verlauf ---------------------------------------------------------
    nichtZuOeffnen: 'Dieser Chat lässt sich nicht öffnen',
    zurueckZurListe: 'Zurück zur Liste',
    zumReiseChat: 'Zurück zum Reise-Chat',
    angabenZurReise: 'Angaben zur Reise',
    wirdGeladen: 'Wird geladen …',
    aeltere: 'Ältere Nachrichten',
    neueNachrichten: 'Neue Nachrichten',
    /**
     * Wenn der Verlauf **nicht geladen werden konnte** — und das ist etwas
     * anderes als „noch nichts geschrieben".
     *
     * Aus dem Betrieb: der Chat blieb bei „Wird geladen …" stehen und sagte
     * danach, es sei nichts da. Beides war falsch: die Abfrage war
     * fehlgeschlagen. Auf der Karte stand in derselben Lage „Diese Reise gibt
     * es nicht" — daran ließ sich weiterarbeiten, hier nicht.
     *
     * **Die Nummer steht dabei.** Sie ist kein Zierrat: 403 heißt etwas
     * anderes als 500, und wer ein Bild davon schickt, hat den Unterschied
     * schon mitgeschickt.
     */
    ladefehler: 'Der Verlauf konnte nicht geladen werden',
    ladefehlerText:
      'Der Server hat die Nachrichten nicht geliefert. Versuch es noch einmal.',
    ladefehlerNummer: (nummer: number) => `Fehler ${nummer}`,
    // Der Knopf heißt `chat.nochmal` -- es gibt ihn schon, für die wartende
    // Nachricht im Ausgangskorb. Ein zweiter mit demselben Wort daneben wäre
    // genau die Doppelung, die diese Datei vermeiden soll.
    keinNetz: 'Kein Netz',
    keinNetzText:
      'Der Verlauf kommt, sobald du wieder Empfang hast. Schreiben kannst du trotzdem, es geht später von selbst raus.',
    nochNichts: 'Noch nichts geschrieben',
    diesePerson: 'Diese Person',
    dieserPerson: 'diese Person',
    nochKeineAntwort: (name: string) => `${name} hat noch nicht geantwortet.`,
    schreibDieErste: 'Schreib die erste Nachricht. Alle Empfänger bekommen sie zu lesen.',
    wartetAufReisenden: 'Sobald der Reisende etwas schreibt, erscheint es hier.',
    nurAn: (name: string) => `Nur an ${name}`,
    aufDerReiseseite: 'Auf der Reiseseite ändern',
    platzhalterAnEinen: (name: string) => [
      `Nachricht nur an ${name}`,
      `Nur an ${name}`,
      'Nur an eine Person',
    ],
    platzhalterUnbekannt: ['Nachricht schreiben', 'Nachricht'],
    platzhalterAnAlle: ['Nachricht an alle Empfänger', 'An alle Empfänger', 'An alle'],
    platzhalterAntwort: (name: string) => [
      `Antwort an ${name}`,
      'Antwort schreiben',
      'Antwort',
    ],
    nichtMehrDabei:
      'Diese Person ist nicht mehr in dieser Reise. Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
    ausgestiegen:
      '{name} ist aus dieser Reise ausgestiegen. Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
    stillgelegt: 'Das Konto von {name} ist stillgelegt und kommt nirgends mehr herein.',
    ausDemVerteiler: '{name} ist aus dem Verteiler genommen und bekommt nichts Neues.',

    // --- Die Sprechblase -----------------------------------------------------
    blaseName: 'Nachricht, lang drücken für das Menü',
    fotoGross: 'Dieses Foto groß ansehen',
    fotoGrossKurz: 'Foto groß ansehen',
    zumBezug: 'Zur bezogenen Nachricht springen',
    geloescht: 'Diese Nachricht wurde gelöscht.',
    nurAnDich: 'Nur an dich',
    anAlleEmpfaenger: 'An alle Empfänger',
    anAlle: 'An alle',
    bearbeitet: ' · bearbeitet',
    nachtraeglichGeaendert: 'Nachträglich geändert',
    auswahlAufheben: 'Auswahl aufheben',
    zumLoeschenWaehlen: 'Zum Löschen auswählen',
    gelesen: 'Gelesen',
    liegtBereit: 'Liegt bereit, noch nicht gelesen',
    vonGelesen: (von: number, alle: number) => `Von ${von} von ${alle} gelesen`,
    menueAntworten: 'Antworten',
    menueKopieren: 'Kopieren',
    menueAufDieKarte: 'Auf die Karte',
    menueAendern: 'Ändern',
    menueLoeschen: 'Löschen',

    // --- Emojis ------------------------------------------------------------
    emojiWahl: 'Emoji auswählen',
    emojiEigenes: 'Anderes Zeichen eingeben',
    emojiPlatzhalter: 'Anderes Zeichen …',
    emojiNurEines: 'Hier passt nur ein Emoji.',
  },

  /**
   * Karte und Highlights.
   *
   * Der größte Zweig, und er hat den größten Bereich hinter sich: siebzehn
   * Dateien, von der Kopfzeile mit den drei Reitern bis zum Formular am
   * Punkt. Gegliedert wird nach dem, was man vor sich sieht, nicht nach
   * Dateinamen — `formular` ist das Blatt am Punkt, `seite` die Kartenseite
   * darunter.
   */
  karte: {
    /** Die drei Reiter unter der Kopfzeile. */
    umschalter: {
      ansicht: 'Ansicht',
      chat: 'Chat',
      karte: 'Karte',
      bearbeiten: 'Bearbeiten',
    },

    /** Die Regler auf der Karte — nur für die Verwaltung. */
    regler: {
      kartenbild: 'Kartenbild',
      festHinweis: 'In der .env ist eine feste Quelle eingetragen (MAP_TILE_URL). Entferne die Zeile, dann kannst du hier wieder wählen.',
      fest: 'Fest aus der .env',
      routenfarbe: 'Farbe der Route',
      strichstaerke: 'Strichstärke der Route',
      farben: 'Farben',
      farbenTitel: 'Farben der Karte',
      voreinstellung: 'Voreinstellung übernehmen',
    },

    /**
     * Die Namen der Farbfelder auf der Karte.
     *
     * Sie standen in `features/map/nadelfarben.ts` neben den CSS-Variablen —
     * dieselbe Aufteilung wie bei den Sprechblasen: die Mechanik bleibt dort,
     * die Wörter kommen hierher.
     */
    nadelFelder: {
      pin_color: 'Freigegeben',
      pin_draft_color: 'Entwurf',
      pin_suggestion_color: 'Vorschlag',
      pin_selected_color: 'Ausgewählt',
      pin_editing_color: 'In Arbeit',
      card_color: 'Kärtchen ohne Foto',
    },

    /** Einen Ort nach seinem Namen finden. */
    ortssuche: {
      titel: 'Ort suchen',
      schliessen: 'Suche schließen',
      feld: 'Ortsname',
      platzhalter: 'Graz, Wuppertal, Pizzeria …',
      suchen: 'Suchen',
      laeuft: '…',
      nichts: 'Dazu findet Apple Maps nichts. Prüf die Schreibweise oder tippe den Ort auf der Karte an.',
      keineAntwort: (status: number) => `Die Suche hat nicht geantwortet (${status}).`,
      offline: 'Ohne Netz gibt es keine Ortssuche. Auf der Karte antippen geht trotzdem.',
      fehlgeschlagen: 'Die Suche hat nicht geklappt.',
      quelleVor: 'Gesucht wird über den Server bei ',
      quelleName: 'Apple Maps',
      quelleNach: '. Dorthin geht dein Suchbegriff, nicht deine Adresse.',
    },

    /**
     * Apples Wetterschlüssel auf Deutsch.
     *
     * Die Zuordnung „welcher Schlüssel bekommt welches Zeichen" bleibt in
     * `lib/wetterlage.ts`; hier stehen nur die Wörter. **Was hier fehlt, wird
     * durchgereicht** — Apple ergänzt die Liste, ohne zu fragen, und ein
     * unbekannter Schlüssel ist wenigstens als solcher zu erkennen.
     */
    wetterlagen: {
      Clear: 'klar',
      MostlyClear: 'überwiegend klar',
      PartlyCloudy: 'teils bewölkt',
      MostlyCloudy: 'überwiegend bewölkt',
      Cloudy: 'bewölkt',
      Drizzle: 'Nieselregen',
      Rain: 'Regen',
      HeavyRain: 'starker Regen',
      Snow: 'Schnee',
      Sleet: 'Schneeregen',
      Hail: 'Hagel',
      Thunderstorms: 'Gewitter',
      Foggy: 'Nebel',
      Haze: 'Dunst',
      Windy: 'windig',
      Breezy: 'frischer Wind',
    },
    /** Die Windrose zu acht Strichen, für „Wind aus SW". */
    himmelsrichtungen: ['N', 'NNO', 'NO', 'ONO', 'O', 'OSO', 'SO', 'SSO', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'],

    /** Fotos und Videos aus der Reise an einen Punkt hängen. */
    fotowahl: {
      titel: 'Aus dieser Reise',
      laedt: 'Wird geladen …',
      allesVergeben: 'Alles aus dieser Reise hängt schon an einem Punkt.',
      nichtsDa: 'In dieser Reise gibt es keine weiteren eigenen Fotos oder Videos. Hier stehen nur deine eigenen aus dem Chat, nicht die der Mitlesenden.',
      passt: 'Passt zu diesem Punkt',
      weitere: 'Weitere aus dieser Reise',
      imChat: 'im Chat',
      nirgends: 'noch nirgends',
      ohneDatum: 'ohne Datum',
      video: 'Video',
      vergeben: (anzahl: number) =>
        anzahl === 1
          ? 'Eines hängt schon an einem anderen Punkt.'
          : `${anzahl} weitere hängen schon an anderen Punkten.`,
      vergebenZusatz: 'Nimm sie dort weg, wenn sie hierher sollen.',
      auswaehlen: 'Auswählen',
      fotosAnhaengen: (anzahl: number) =>
        anzahl === 1 ? '1 Foto anhängen' : `${anzahl} Fotos anhängen`,
      videosAnhaengen: (anzahl: number) =>
        anzahl === 1 ? '1 Video anhängen' : `${anzahl} Videos anhängen`,
      gemischtAnhaengen: (anzahl: number) => `${anzahl} anhängen`,
    },

    /** Die Sammelfreigabe unter der Karte. */
    freigabe: {
      falte: (offene: string) => `Freigeben · ${offene}`,
      entwuerfe: (anzahl: number) => (anzahl === 1 ? '1 Entwurf' : `${anzahl} Entwürfe`),
      vorschlaege: (anzahl: number) =>
        anzahl === 1 ? '1 Vorschlag' : `${anzahl} Vorschläge`,
      einerVonMehreren: (betroffen: number, alle: number) =>
        alle === 1 ? '1 Entwurf' : `${betroffen} von ${alle} Entwürfen`,
      bis: ' bis',
      bisLabel: 'Freigeben bis',
      laeuft: 'Läuft …',
      freigebenMit: (anzahl: number) => `Freigeben (${anzahl})`,
      ergebnis: (freigegeben: number, ohneZeit: number) =>
        `${freigegeben} freigegeben` +
        (ohneZeit > 0 ? `, ${ohneZeit} ohne Zeitangabe liegen geblieben` : ''),
      nichtDabei: (anzahl: number) =>
        anzahl === 1
          ? 'Ein Vorschlag aus der Route ist nicht dabei'
          : `${anzahl} Vorschläge aus der Route sind nicht dabei`,
      nichtDabeiZusatz:
        '. Zum Freigeben erst öffnen und sichern, dann wird daraus ein Entwurf.',
      sammelbeitrag: 'Ab sechs Highlights steht im Chat eine Zusammenfassung statt einzelner Beiträge.',
      ohneZeitKopf: 'Ohne Zeitangabe, deshalb nicht von „Freigeben bis" erfasst:',
      zeitNachtragen: 'Zeit nachtragen',
      trotzdemFreigeben: 'Trotzdem freigeben',
    },

    /** Die Detailkarte zu einem Punkt. */
    blatt: {
      vorheriger: 'Vorheriger Punkt',
      naechster: 'Nächster Punkt',
      ohneZeit: 'ohne Zeitangabe',
      entwurf: 'Entwurf',
      vorschlag: 'Vorschlag',
      entwurfTitel: 'Nur für dich sichtbar',
      vorschlagTitel: 'Aus der Aufzeichnung, nur für dich sichtbar',
      freigeben: 'Freigeben',
      verbergen: 'Verbergen',
      verbergenFrage: (titel: string) =>
        `„${titel}" wieder verbergen? Der Beitrag dazu im Chat bleibt stehen.`,
      aendern: 'Ändern',
      aendernLabel: (titel: string) => `${titel} ändern`,
      wegnehmen: 'Wegnehmen',
      wegnehmenLabel: (titel: string) => `${titel} wegnehmen`,
      wegnehmenFrage: (titel: string) => `„${titel}" wegnehmen?`,
    },

    /** Der Stapel der Kärtchen unter der Karte. */
    stapel: {
      leer: 'Noch keine Highlights. Nimm einen der Knöpfe auf der Karte: „Highlight", „Hier" oder „Aus Foto/Video".',
      anzahl: (anzahl: number) => (anzahl === 1 ? '1 Highlight' : `${anzahl} Highlights`),
      offeneAnhang: (worte: string) => ` · ${worte}`,
      ohneZeit: 'ohne Zeitangabe',
      entwurf: 'Entwurf',
      vorschlag: 'Vorschlag',
    },

    /** Die Landkarte selbst — Knöpfe darauf und ihre Meldungen. */
    flaeche: {
      ganzeReise: 'Ganze Reise zeigen',
      punktSetzen: 'Highlight',
      tippen: 'Tippe auf die Karte',
      hier: 'Hier',
      ortet: 'Wird geortet …',
      keinePosition: 'Die Position kam nicht.',
      ausFoto: 'Aus Foto/Video',
      ortSuchen: 'Ort suchen',
    },

    /** Die Kartenseite: Kopfzeile, Leerzustände, GPX-Leiste. */
    seite: {
      zurueck: 'Zurück zur Liste',
      zurReiseseite: 'Zur Reiseseite',
      route: 'Route',
      routeMitDateien: (anzahl: number) =>
        `Route · ${anzahl} ${anzahl === 1 ? 'GPX-Datei' : 'GPX-Dateien'}`,
      punkte: (anzahl: number) => `${anzahl} ${anzahl === 1 ? 'Highlight' : 'Highlights'}`,
      abgeschlossen:
        'Der Chat dieser Reise ist geschlossen. Die Karte kannst du weiter bearbeiten. Freigaben erscheinen dann nicht im Chat.',
      laedt: 'Wird geladen …',
      routeFehlt: 'Die Route lässt sich nicht laden',
      leerTitel: 'Noch kein Kartenpunkt gesetzt',
      leerAutor:
        'Setz einen Kartenpunkt oder importiere eine Route als GPX-Datei, zum Beispiel aus myTracks, Komoot, OsmAnd oder Garmin.',
      leerLeser:
        'Sobald der Reisende einen Kartenpunkt setzt oder eine Route importiert, steht das hier.',
      wartenAufNetz: (anzahl: number) =>
        anzahl === 1 ? '1 Highlight wartet auf Netz.' : `${anzahl} Highlights warten auf Netz.`,
      videoBrauchtNetz: 'Ein Video braucht Netz, ein Foto geht auch ohne.',
      bildUnlesbar: 'Ein Bild ließ sich nicht lesen.',
      keinePosition: 'Die Position kam nicht.',
      importLaeuft: 'Wird gelesen …',
      importieren: 'GPX-Route importieren',
      leereWerkbank:
        'Noch kein Kartenpunkt gesetzt. „Hier" nimmt deinen Standort, „Aus Foto/Video" den Ort der Aufnahme. Oder importiere eine GPX-Datei aus myTracks, Komoot, OsmAnd oder Garmin.',
      nachleseLaeuft: (fertig: number, gesamt: number) =>
        `Wird gesucht … ${fertig} von ${gesamt}`,
      nachlese: 'Aufenthalte in der Route suchen',
      offeneAngaben: (anzahl: number) => `Ort und Wetter werden geholt, noch ${anzahl}.`,
      dateien: (anzahl: number) => (anzahl === 1 ? '1 GPX-Datei' : `${anzahl} GPX-Dateien`),
      dateiZahlen: (behalten: number, roh: number, groesse: string) =>
        `${behalten} von ${roh} Punkten · ${groesse}`,
      dateiEntfernen: 'GPX-Datei entfernen',
      dateiEntfernenFrage: (name: string) => `„${name}" aus der Route entfernen?`,
      entfernen: 'Entfernen',
      ohneZeitangabe: 'ohne Zeitangabe',
      zeitraum: (von: string, bis: string) => `${von} – ${bis}`,
    },

    /**
     * Was die Aufenthaltserkennung gesehen hat — fünf Lagen, nicht eine.
     *
     * „Keine längeren Aufenthalte" stand einmal für alles: nichts gefunden,
     * knapp verfehlt, schon vorhanden, Datei ohne Zeitstempel. Die Trennung
     * ist der Sinn dieser Sätze, und sie überlebt den Umzug.
     */
    befund: {
      ausDateien: (anzahl: number) => `aus ${anzahl} Dateien`,
      ausRoute: 'aus der Route',
      vorschlaege: (anzahl: number, wo: string) =>
        `${anzahl} ${anzahl === 1 ? 'Vorschlag' : 'Vorschläge'} ${wo}, als ${anzahl === 1 ? 'Entwurf' : 'Entwürfe'} unter der Karte.`,
      schonDa: (anzahl: number) =>
        anzahl === 1
          ? '1 Aufenthalt erkannt, dort steht schon ein Highlight.'
          : `${anzahl} Aufenthalte erkannt, dort stehen schon Highlights.`,
      ohneZeitstempelMehrere:
        'Keine dieser Dateien enthält Zeitstempel. Ohne sie lassen sich keine Aufenthalte erkennen.',
      ohneZeitstempelEine:
        'Diese Datei enthält keine Zeitstempel. Ohne sie lassen sich keine Aufenthalte erkennen.',
      knappVerfehlt: (minuten: number, schwelle: number, meter: number) =>
        `Nichts gefunden. Der längste Halt dauerte ${minuten} Min., gesucht wird ab ${schwelle} Min. im Umkreis von ${meter} m.`,
      abgeschaltet: 'Die Aufenthaltserkennung ist abgeschaltet (0 Minuten in den Einstellungen).',
      nichtsMehrere: 'Keine weiteren Aufenthalte in diesen Aufzeichnungen.',
      nichtsEine: 'Keine längeren Aufenthalte in dieser Aufzeichnung.',
      rueckwaerts: (anzahl: number) =>
        `${anzahl} Punkte mit rückwärts laufender Zeit übergangen.`,
    },

    /**
     * Warum aus einer Aufnahme kein Ort kam — **und woran es lag**.
     *
     * Ein Satz für alle Fälle hat hier einen Fehler zwei Runden lang
     * versteckt: eine Datei, die der Leser gar nicht öffnen konnte, sah aus
     * wie ein Bild ohne Angaben.
     */
    ohneOrt: {
      nachsatz: ' Zieh den Punkt auf die Karte oder nimm „Hier".',
      diesesVideo: 'Dieses Video',
      diesesFoto: 'Dieses Foto',
      dieseAufnahme: 'Diese Aufnahme',
      keineOrtsangabe: (das: string) => `${das} trägt keine Ortsangabe.`,
      videoOhneDaten: (das: string) =>
        `${das} enthält keine Aufnahmedaten mehr, beim Exportieren gehen sie oft verloren. Über die Dateien-App statt über Fotos bekommst du das Original.`,
      fotoOhneDaten: (das: string) =>
        `${das} enthält keine Aufnahmedaten mehr, beim Weiterleiten oder Speichern gehen sie oft verloren.`,
      unlesbar: (wovon: string) => `Die Aufnahmedaten ${wovon} ließen sich nicht lesen.`,
      diesesVideosGenitiv: 'dieses Videos',
      diesesFotosGenitiv: 'dieses Fotos',
      garNichts: 'Aus dieser Datei lassen sich keine Aufnahmedaten lesen.',
    },

    /** Das Formular am Punkt — anlegen und ändern. */
    formular: {
      anlegenLabel: 'Highlight anlegen',
      aendernLabel: 'Highlight ändern',
      neu: 'Neues Highlight',
      aendern: 'Highlight ändern',
      karteGross: 'Karte groß',
      formularZurueck: 'Formular zurück',
      titel: 'Titel',
      text: 'Text',
      wann: 'Wann war das?',
      gecraetezeit: 'Gerätezeit',
      reisezeit: (versatz: string) => `Reise (${versatz})`,
      zone: 'Zeitzone dieses Punktes',
      wo: 'Wo war das?',
      koordinaten: (lat: string, lon: string, herkunft: string) => `${lat}, ${lon} · ${herkunft}`,
      ziehbar: 'Auf der Karte lässt sich der Punkt ziehen.',
      hier: 'Hier',
      ortet: 'Wird geortet …',
      keinePosition: 'Die Position kam nicht.',
      angabenHolen: 'Ort und Wetter holen',
      angabenNeuHolen: 'Ort und Wetter neu holen',
      angabenLaufen: 'Wird geholt …',
      inDerNaeheFinden: 'In der Nähe finden',
      nichtsZurueck: 'Zu diesem Punkt gibt es keine Angaben.',
      routeSuchen: 'Auf die Route legen',
      routeLaeuft: 'Wird gesucht …',
      keineRoute: 'Hier ist keine Route in der Nähe.',
      abstand: (meter: number) => `${meter} m entfernt`,
      uebernehmen: 'Übernehmen',
      lieberNicht: 'Lieber nicht',
      medien: 'Fotos und Videos',
      video: 'Video',
      wiederEntfernen: 'Wieder entfernen',
      loeschenFrage: 'Wirklich löschen? Das lässt sich nicht rückgängig machen.',
      loeschen: 'Löschen',
      rechnet: 'rechnet',
      laedt: 'lädt',
      anteil: (prozent: number) => `${prozent} %`,
      ausbeute: (was: string) => `Video: ${was}`,
      ausFotoApp: 'Aus der Foto-App',
      ausDieserReise: 'Aus dieser Reise',
      imChatErzaehlen: 'Bei der Freigabe im Chat erzählen',
      chatZu: 'Der Chat dieser Reise ist geschlossen. Erzählt wird nur bei einer Freigabe, wenn der Chat wieder offen ist. Auf der Karte steht der Punkt trotzdem.',
      entwurfWort: 'Entwurf',
      fussnoteOnlineVor: 'Ein neues Highlight ist ein ',
      fussnoteOnlineNach: ' und nur für dich sichtbar, bis du es freigibst.',
      fussnoteOfflineVor: 'Ohne Netz. Das Highlight wird gesichert und geht raus, sobald wieder Netz da ist, als ',
      fussnoteOfflineNach: ', wie jedes neue Highlight.',
      speichertLaeuft: 'Wird gespeichert …',
      inDenAusgangskorb: 'In den Ausgangskorb',
      alsEntwurf: 'Als Entwurf',
      sichern: 'Sichern',
      freigebenTitel: 'Sichern und für alle freigeben',
      freigebenLaeuft: 'Wird freigegeben …',
      freigeben: 'Freigeben',
      keinBild: 'Das ist weder ein Bild noch ein Video.',
      bildNichtVorbereitet: 'Das Bild ließ sich nicht vorbereiten.',
      videoBrauchtNetz: 'Ein Video braucht Netz, ein Foto geht auch ohne.',
      videoNichtDurch: 'Das Video ging nicht durch.',
      bildNichtDurch: 'Das Bild ging nicht durch.',
      titelFehlt: 'Ein Highlight braucht einen Titel.',
      freigabeMisslungen: (grund: string) =>
        `Gesichert, aber die Freigabe hat nicht geklappt: ${grund}`,
      freigabeMisslungenOhneGrund: 'Gesichert, aber die Freigabe hat nicht geklappt.',
    },

    /** Woher Ort und Zeit eines Punktes stammen — „aus dem Foto", „von Hand". */
    herkunft: {
      ortFoto: 'aus dem Foto',
      ortVideo: 'aus dem Video',
      ortPoi: 'aus der Karte gewählt',
      ortTrack: 'von der Route',
      ortGeraet: 'vom Gerät',
      ortKarte: 'auf der Karte gesetzt',
      zeitOhne: 'Ohne Zeitangabe. Nachtragen geht später.',
      zeitFoto: 'Aus den Aufnahmedaten des Fotos.',
      zeitVideo: 'Aus den Aufnahmedaten des Videos.',
      zeitTrack: 'Aus der Aufzeichnung.',
      zeitGeraet: 'Von diesem Gerät.',
      zeitManuell: 'Von Hand gesetzt.',

      /**
       * **Woher die UHR kommt — getrennt von der Zeit** (#74).
       *
       * In der Oberfläche heißt sie nicht so. Aus dem Betrieb (0.26.2):
       * „statt ‚Uhr aus der Aufnahme' → ‚Zeit aus der Aufnahme'." Die
       * Unterscheidung Uhr/Uhrzeit trägt der Kommentar, nicht der Satz —
       * ein Mensch liest „Zeit" und versteht Stunde samt Zone.
       *
       * Aus dem Betrieb, mit drei Fotos belegt: unter allen dreien stand
       * „Aus den Aufnahmedaten des Fotos". Für die *Uhrzeit* stimmte das
       * jedes Mal; für die *Uhr* nur bei zweien. Beim dritten (Kamera, 2021)
       * stand keine Zone in der Datei, und eingesprungen ist das Gerät —
       * angesagt hat es niemand.
       *
       * **Eine Vermutung, die aussieht wie eine Auskunft, ist schlechter als
       * eine, die sich zu erkennen gibt.**
       */
      uhrFoto: 'Zeit aus der Aufnahme.',
      uhrReise: 'Zeit aus der Reise.',
      uhrGeraet: 'Zeit dieses Geräts, die Aufnahme sagt nichts dazu.',

      /**
       * **Wenn Kamera und Reise auseinandergehen** (#74).
       *
       * Aus dem Betrieb: ein Foto aus New York trug `UTC+2`, weil die Kamera
       * auf deutscher Zeit stand und nie umgestellt wurde. Die App hat richtig
       * gelesen — die Kamera hatte unrecht. Auffallen konnte es niemandem,
       * weil nichts die beiden Angaben nebeneinanderstellte.
       *
       * **Das Foto gewinnt trotzdem** (so entschieden): die Kamera war dort,
       * die Reiseeinstellung ist eine Angabe von früher. Aber es steht jetzt
       * da, und ein Griff genügt zum Ändern.
       */
      uhrWeicht: (kamera: string, reise: string) =>
        `Die Aufnahme sagt ${kamera}, die Reise ${reise}. Übernommen ist die der Aufnahme.`,
    },

    /** Ein Spot für sich — und die Liste, die zu ihm führt. */
    spots: {
      titel: 'Spots & Aktivitäten',
      lead: 'Restaurants, Stellplätze, Museen: was sonst noch da war.',
      ansehen: 'Spot ansehen',
      mitText: 'mit Text',
      wegnehmen: 'Wegnehmen',
      wegnehmenLabel: (name: string) => `${name} wieder wegnehmen`,
      hinzufuegen: 'Spot hinzufügen',
      wasWarNochDa: 'Was war noch da?',
      /** Ein Spot ohne eine der acht Marken — Apple kennt mehr, als hier steht. */
      ohneMarke: 'Spot',
      entfernung: (weit: string) => `${weit} vom Punkt`,
      wasWarHier: 'Was war hier?',
      notizPlatzhalter: 'Bestes Steak der Reise, Tisch draußen mit Blick auf den Fjord …',
      notizZusatz: 'gesichert wird, wenn du das Feld verlässt',
      keinText: 'Zu diesem Spot steht noch kein Text.',
      aufDerKarte: 'Auf der Karte ansehen',
    },

    /** „In der Nähe finden" — die Auswahl und ihre Treffer. */
    umgebung: {
      titel: 'In der Nähe finden',
      hinweis: 'Gesucht wird rund um den Punkt im Formular. Wählst du etwas aus, wandern Titel und Nadel dorthin.',
      nameSuchen: 'Oder nach dem Namen suchen',
      platzhalter: 'Graz, Wuppertal, Pizzeria …',
      suchen: 'Suchen',
      quelle: 'Gesucht wird über den Server bei Apple Maps, im selben Umkreis wie die Kacheln.',
      andereSuche: 'Andere Suche',
      begriff: (wort: string) => `„${wort}"`,
      laeuft: 'Wird gesucht …',
      keineAntwort: 'Die Suche hat nicht geantwortet. Versuch es später noch einmal.',
      nichts: 'Hier wurde nichts gefunden. Probier eine andere Kategorie oder einen größeren Umkreis in den Einstellungen.',
      weiter: (meter: number) => `Im Umkreis von ${meter} m war nichts. Das hier liegt weiter weg.`,
      fehlgeschlagen: 'Die Suche hat nicht geklappt.',
    },

    /**
     * Die acht Marken der Umgebungssuche.
     *
     * Der deutsche Name steht hier, die Apple-Kategorie und das Zeichen
     * bleiben in `Umgebungswahl.tsx`. Wer eine Kachel ergänzt, ergänzt beides
     * — aber das Wort nur einmal.
     */
    marken: {
      essen: 'Essen & Trinken',
      abend: 'Bar & Abend',
      kultur: 'Kultur',
      sehenswert: 'Sehenswürdigkeit',
      natur: 'Natur & Aussicht',
      uebernachten: 'Übernachtung',
      camping: 'Camping & Stellplatz',
      laden: 'Markt & Laden',
    },
  },

  /** Was überall gleich heißt. */
  allgemein: {
    speichern: 'Speichern',
    abbrechen: 'Abbrechen',
    verwerfen: 'Verwerfen',
    schliessen: 'Schließen',
    laeuft: 'Einen Moment …',
    gespeichert: 'Gespeichert.',
    schiefgelaufen: 'Das hat nicht geklappt.',
    ohneNetz: 'Dafür brauchst du Netz. Versuch es später noch einmal.',
    ja: 'Ja',
    name: 'Travel Broadcast',
    /**
     * Das Sprachgebiet für `Intl` und `toLocaleDateString`.
     *
     * **Es gehört neben die Wörter und nicht in den Code**, denn mit der
     * Sprache wechselt auch das Format: „5. Okt. 2026" wird „Oct 5, 2026",
     * und die Reihenfolge von Tag und Monat ist keine Kleinigkeit. Es stand
     * bis `0.16.0` neunmal in `lib/` und einmal in der Karte; jetzt einmal
     * hier.
     */
    gebiet: 'de-DE',
  },

  /**
   * Wörter aus `lib/` und `api/` — Stellen ohne eine einzige Komponente.
   *
   * Sie holen ihren Wortlaut über `WORTLAUT` statt über `useTexte()`; das ist
   * die eine erlaubte Ausnahme, und `tools/pruefstand/texte.ts` führt Buch
   * darüber, wer sie nimmt und warum.
   *
   * **Nicht hier steht die Diagnosespur.** `notiere('das Element meldet keine
   * Masse')` schreibt in den Mitschrieb unter *Einstellungen › Diagnose* — ein
   * Werkzeug für die Fehlersuche, das mit derselben Sorgfalt zu übersetzen
   * wäre wie ein Stacktrace. Die Trennung ist im Bestand schon sichtbar:
   * **Oberflächentext fängt groß an und hört mit einem Punkt auf**, ein
   * Diagnosestück nicht.
   */
  /**
   * Was dasteht, wenn beim Zeichnen etwas wirft (`components/Absturz.tsx`).
   *
   * **Der Anlass war ein weißer Bildschirm** — die schlimmste Form der stillen
   * Absage: aus ihm lässt sich nicht ablesen, was schiefging, in welchem Stand,
   * und ob es am Gerät liegt oder am Bau.
   */
  absturz: {
    titel: 'Da ist etwas schiefgegangen',
    lead: 'Die App konnte diese Seite nicht anzeigen. Oft liegt es an einem veralteten Zwischenspeicher, besonders in der App auf dem Home-Bildschirm.',
    stand: (bau: string) => `Oberfläche ${bau}`,
    neuLaden: 'Zwischenspeicher leeren und neu laden',
    hinweis: 'Ungesendete Nachrichten bleiben erhalten. Hilft es nicht, schick ein Bildschirmfoto dieser Seite.',
  },

  technik: {
    /** Zeitangaben, wie ein Messenger sie zeigt (`lib/format.ts`). */
    zeit: {
      heute: 'Heute',
      gestern: 'Gestern',
      /**
       * Die zweite Uhrzeit an einer Sprechblase — **die des Absenders** (#71).
       *
       * Steht nur da, wenn sie von der eigenen abweicht. Zweimal dieselbe Zahl
       * nebeneinander wäre kein Hinweis, sondern Lärm.
       *
       * **„vor Ort" und nicht „dort":** die Blase steht mal links, mal rechts,
       * und „dort" müsste je nach Seite etwas anderes heißen. „Vor Ort" meint
       * immer denselben Ort — den, an dem geschrieben wurde.
       */
      vorOrt: (uhrzeit: string) => ` · ${uhrzeit} vor Ort`,
      /**
       * Die Monatsnamen — ausgeschrieben, weil hier von Hand zerlegt wird.
       *
       * `new Date('2026-06-12')` liest der Browser als Mitternacht UTC und
       * zeigt westlich davon den Vortag; deshalb kommt `Intl` für den
       * Reisezeitraum nicht in Frage. Das Zerlegen bleibt in `format.ts`, die
       * zwölf Wörter stehen hier.
       */
      monate: [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
      ],
      ab: (datum: string) => `ab ${datum}`,
      bis: (datum: string) => `bis ${datum}`,
      spanne: (von: string, bis: string) => `${von} – ${bis}`,
      spanneImMonat: (tag: string, bis: string) => `${tag}.–${bis}`,
      /** Wie ein Medium heißt, wenn kein Text danebensteht. */
      foto: 'Foto',
      video: 'Video',
      sprachnachricht: 'Sprachnachricht',
      /** Ohne Namen wären die Initialen leer — ein Fragezeichen sagt mehr. */
      ohneNamen: '?',
      /** Welche Uhr gilt (`lib/ortszeit.ts`). */
      geraetezeit: 'Gerätezeit',
      ortszeit: (versatz: string) => `Ortszeit ${versatz}`,
    },

    /** Was schiefgehen kann, und was dann dasteht. */
    fehler: {
      serverfehler: 'Der Server hat einen Fehler gemeldet.',
      unlesbar: 'Die Antwort des Servers war unlesbar.',
      hochladenAbgebrochen: 'Das Hochladen wurde abgebrochen.',
      keineAntwort: 'Der Server hat nicht rechtzeitig geantwortet.',
      indexeddbBelegt: 'IndexedDB ist von einem anderen Fenster belegt.',
      bildNichtVerkleinern: 'Dieser Browser kann das Bild nicht verkleinern.',
      bildNichtUmwandeln: 'Das Bild ließ sich nicht umwandeln.',
      bildNichtOeffnen: 'Dieses Bild lässt sich nicht öffnen.',
    },

    /** Warum keine Position kam (`lib/ortung.ts`). */
    ortung: {
      kannNicht: 'Dieses Gerät kann seine Position nicht bestimmen.',
      nichtErlaubt:
        'Die Ortung ist für diese Seite nicht erlaubt. Erlaube sie in den Einstellungen des Browsers.',
      zuSpaet: 'Die Position kam nicht rechtzeitig. Unter freiem Himmel klappt es meist.',
      misslungen: 'Die Position ließ sich nicht bestimmen.',
    },

    /** Benachrichtigungen einrichten (`lib/push.ts`). */
    push: {
      nichtEingerichtet: 'Auf diesem Server sind Benachrichtigungen nicht eingerichtet.',
      abgelehnt:
        'Die Erlaubnis wurde abgelehnt. Sie lässt sich in den Einstellungen des Browsers wieder erteilen.',
      unbeantwortet:
        'Die Frage nach der Erlaubnis blieb unbeantwortet. Am Rechner zeigt der Browser sie oft als kleines Symbol links in der Adresszeile. Dort kannst du sie nachholen.',
      keinAbonnement: (grund: string) =>
        'Der Browser konnte sich nicht anmelden. Auf dem iPhone geht das nur in der '
        + 'installierten App (Teilen → Zum Home-Bildschirm). '
        + `Sonst hilft ein Blick in die Browser-Einstellungen. (${grund})`,
      unbekannterFehler: 'unbekannter Fehler',
    },

    /** Video: umrechnen, hochladen, ein Standbild finden. */
    video: {
      browserKannNicht: 'Dieser Browser kann Videos nicht umrechnen.',
      nichtUmgerechnet: 'Das Video ließ sich nicht umrechnen.',
      keinH264: 'Dieses Gerät kann kein H.264 schreiben.',
      keineLeinwand: 'Das Bild ließ sich nicht zeichnen.',
      umrechnenAbgebrochen: 'Das Umrechnen wurde abgebrochen.',
      keineVerwaltungsdaten: 'Der Aufbau dieser Videodatei ließ sich nicht lesen.',
      keineBildspur: 'In dieser Datei ist keine Bildspur.',
      /**
       * Der Grund vom Browser steht **in Klammern hinter** dem deutschen Satz.
       *
       * Aus dem Betrieb, am Bildschirmfoto: „Decoder failure Ohne Umrechnung
       * wuerde es auf manchen Geraeten nicht abspielen." Angeklebt ergab das
       * einen Satz, der wie ein Fehler im Text aussieht.
       */
      keinFormat:
        'Dieses Video lässt sich auf diesem Gerät nicht in ein Format umrechnen, das alle abspielen.',
      nichtUmgerechnetMitGrund: (grund: string) =>
        `Dieses Gerät konnte das Video nicht umrechnen (${grund}).`
        + ' Ohne Umrechnung würde es auf manchen Geräten nicht abspielen.',
      keineMasse: 'Dieses Video meldet seine Maße nicht.',
      nichtOeffnen: 'Dieses Video lässt sich nicht öffnen.',
      nichtSpulbar: 'Diese Aufnahme lässt sich nicht spulen.',
      nichtGespult: 'Diese Aufnahme ließ sich nicht spulen.',
      spulenFehlgeschlagen: 'Das Video ließ sich nicht spulen.',
    },

    /**
     * Die Reiter der Emoji-Auswahl (`lib/emojiliste.ts`).
     *
     * Der Reiter trägt ein Zeichen und kein Wort — acht Wörter wären breiter
     * als ein Telefon. Das Wort steht daneben, für Vorlesehilfen und den
     * Tooltip, und der Schlüssel holt es hier ab: dieselbe Aufteilung wie bei
     * den Nadelfarben und den Marken der Umgebungssuche.
     */
    emojigruppen: {
      gesichter: 'Gesichter',
      gesten: 'Gesten',
      herzen: 'Herzen und Zeichen',
      unterwegs: 'Unterwegs',
      natur: 'Natur und Wetter',
      essen: 'Essen und Trinken',
      anlaesse: 'Anlässe und Dinge',
      fahnen: 'Fahnen',
    },
  },

  /** Die Reiseübersicht — die Liste, mit der die Anwendung aufgeht. */
  liste: {
    titel: 'Reisen',
    neueReise: 'Neue Reise',
    einstellungen: 'Einstellungen',
    laedt: 'Wird geladen …',
    /** Kein Netz ist kein Ausfall — deshalb ein ruhiger Satz statt einer Meldung. */
    keinNetz: 'Kein Netz',
    keinNetzLead:
      'Die Liste kommt, sobald du wieder Empfang hast. Was du bis dahin schreibst, geht später von selbst raus.',
    fehlt: 'Die Liste ließ sich nicht laden',
    schemaVor: 'Der Datenbank fehlt, was diese Fassung erwartet: ',
    schemaNach: '. Die Anweisung dazu steht in ',
    schemaDatei: 'docs/deployment-all-inkl.md',
    schemaPunkt: '.',
    keineReise: 'Noch keine Reise',
    keineReiseLead:
      'Lege eine Reise an und teile den Einladungslink mit denen, die mitlesen sollen.',
    reiseAnlegen: 'Reise anlegen',
    nichtsZuLesen: 'Noch nichts zu lesen',
    nichtsZuLesenLead: 'Sobald dich jemand zu einer Reise einlädt, erscheint sie hier.',

    /**
     * Die drei Abschnitte am Ende der Liste.
     *
     * *Chat geschlossen* heißt: im Verlauf kommt nichts mehr dazu, gelesen
     * wird weiter. *Nur für dich* heißt: die Reise ist aus den Listen aller
     * anderen verschwunden. Zwei Stufen, zwei Namen.
     */
    aktiv: 'Aktiv',
    weitereReisen: 'Weitere Reisen',
    chatGeschlossen: 'Chat geschlossen',
    nurFuerDich: 'Nur für dich',

    verlaeufeZeigen: 'Antwortverläufe zeigen',
    verlaeufeEinklappen: 'Antwortverläufe einklappen',
    verlaeufeZeigenVon: (reise: string) => `Antwortverläufe von ${reise} zeigen`,
    verlaeufeEinklappenVon: (reise: string) => `Antwortverläufe von ${reise} einklappen`,

    /** Eine Zeile je Mitlesendem unter seiner Reise. */
    person: {
      stillgelegt: 'stillgelegt',
      nichtMehrDabei: 'nicht mehr dabei',
      pausiert: 'pausiert',
      pausiertTitel: 'Bekommt nichts Neues',
      beitraegeVon: (anzahl: number) => `${anzahl} Beiträge von dieser Person`,
      beitraegeGelesen: (anzahl: number) =>
        anzahl === 1 ? ' Beitrag geschrieben' : ' Beiträge geschrieben',
      reaktionenTitel: (anzahl: number) => `${anzahl} Reaktionen`,
      reaktionen: ' Reaktionen',
      allesGelesen: 'Hat alles gelesen',
      bereitgelegt: 'Bereitgelegt, noch nicht gelesen',
      nochNichtGelesen: 'Noch nicht gelesen',
    },

    ungelesen: ' ungelesene Nachrichten',
    ungelesenStumm: ' ungelesene Nachrichten, stumm',
    stumm: 'Stummgeschaltet',
    duDoppelpunkt: 'Du: ',
    keineNachricht: 'Noch keine Nachricht',
  },

  /** Eine Reise anlegen, einrichten und verwalten — die Reiseseite. */
  reise: {
    /** Eine neue Reise. */
    neu: {
      titel: 'Neue Reise',
      zurueck: 'Zurück',
      lead: 'Du bist der Absender. Was du schreibst, lesen alle Empfänger. Ihre Antworten liest nur du.',
      feldTitel: 'Titel',
      platzhalter: 'z. B. Norwegen 2026',
      beschreibung: 'Beschreibung (kann leer bleiben)',
      von: 'Von (kann leer bleiben)',
      bis: 'Bis',
      wirdAngelegt: 'Wird angelegt …',
      anlegen: 'Reise anlegen',
    },

    /** Das Symbolbild in der Übersicht. */
    symbolbild: {
      titel: 'Symbolbild',
      lead: 'Was in der Übersicht vor dem Titel steht. Ohne Bild stehen dort die Anfangsbuchstaben.',
      anderesBild: 'Anderes Bild',
      bildWaehlen: 'Bild wählen',
      ausschnitt: 'Ausschnitt wählen',
      ausschnittTitel: 'Ausschnitt des Symbolbildes',
      wegnehmen: 'Wegnehmen',
      wegFrage: 'Das Symbolbild dieser Reise wegnehmen?',
      bildFehlgeschlagen: 'Das Bild ging nicht durch.',
    },

    /** Der Hintergrund des Chats — eine Farbe, ein Bild, oder beides. */
    grund: {
      titel: 'Hintergrund im Chat',
      lead: 'Gilt für alle, die diese Reise mitlesen. Eine Farbe, ein Bild oder beides.',
      /** Die sechs Vorschläge — auf Lesbarkeit hin gewählt. */
      farben: {
        ohne: 'Ohne',
        sand: 'Sand',
        salbei: 'Salbei',
        nebel: 'Nebel',
        rose: 'Rosé',
        nacht: 'Nacht',
      },
      eigeneFarbe: 'Eigene Farbe',
      farbHinweis: 'Sechs Vorschläge, oder rechts eine eigene Farbe.',
      zurzeit: (farbe: string) => ` Zurzeit ${farbe}.`,
      deckgrad: (prozent: number) => `Deckgrad des Bildes: ${prozent} %`,
      ausschnittTitel: 'Ausschnitt des Hintergrunds',
    },

    /**
     * Die Reiseseite — Angaben, Buch, Einladungen, Empfänger, Zustand.
     *
     * Die größte Datei der Anwendung und der größte Zweig hier. Gegliedert
     * nach den Kategorien der Seite, so wie sie einer vor sich sieht.
     */
    info: {
      /** Die Kategorien in der Leiste. Der Schlüssel steht in der Adresse. */
      kategorien: {
        reise: 'Reise',
        aussehen: 'Aussehen',
        benachrichtigung: 'Benachrichtigung',
        einladen: 'Einladen',
        anfragen: 'Anfragen',
        empfaenger: 'Empfänger',
        willkommen: 'Willkommensnachricht',
        zuruecksetzen: 'Zurücksetzen',
        verlassen: 'Diese Reise verlassen',
      },
      zurueck: 'Zurück',

      /** Angaben zur Reise und das Formular dahinter. */
      angabenAendern: 'Angaben ändern',
      duBistAbsender: 'Du bist der Absender dieser Reise.',
      berichtVon: (wer: string) => `Reisebericht von ${wer}.`,
      feldTitel: 'Titel',
      beschreibung: 'Beschreibung',
      von: 'Von',
      bis: 'Bis',
      zeitzone: 'Zeitzone der Reise',
      zeitzoneOhne: 'Keine Angabe, Zeit des jeweiligen Geräts',
      zeitzoneHinweis: 'Zeiten an Highlights stehen dann in dieser Zeitzone, bei allen gleich.',
      /**
       * Der Knopf neben der Liste — er trägt die Uhr, die er setzen wird.
       *
       * Aus dem Betrieb gewünscht: „neben der manuellen Einstellung noch
       * einen Knopf ‚Zeitzone des aktuellen Orts' übernehmen." Wer weiterfährt
       * und abends die Reise nachstellt, sucht sonst in einer Liste von
       * siebenunddreißig Einträgen die eine, auf der sein eigenes Telefon
       * ohnehin schon steht. **Die Klammer nennt sie**, damit der Knopf nicht
       * blind drückt.
       */
      zeitzoneHier: (uhr: string) => `Zeitzone des aktuellen Orts (${uhr})`,
      zeitzoneHierGesetzt: 'Übernommen, noch nicht gespeichert.',
      wirdGespeichert: 'Wird gespeichert …',

      /**
       * Die drei Zustände einer Reise, als Leiter.
       *
       * Jeder nimmt etwas mehr weg, und keiner löscht etwas. Die Rückfrage
       * steht dort, wo etwas verschwindet — beim Öffnen und beim Schließen
       * braucht es keine, beim Zurückziehen schon.
       */
      zustandTitel: 'Zustand der Reise',
      zustand: {
        active: {
          titel: 'Läuft',
          text: 'Du schreibst Beiträge, die Empfänger antworten.',
        },
        closed: {
          titel: 'Chat geschlossen',
          text: 'Niemand schreibt mehr, auch du nicht. Lesen können den Bericht weiter alle, und die Karte kannst du weiter bearbeiten.',
          frage:
            'Den Chat dieser Reise schließen?\n\nDanach kann niemand mehr schreiben, auch du nicht. Lesen können den Bericht weiter alle.\n\nDie Karte bleibt offen. Freigaben erscheinen dann aber nicht mehr im Chat.',
        },
        private: {
          titel: 'Nur noch für mich',
          text: 'Die Reise verschwindet aus den Listen aller Mitlesenden, mit Chat und Karte. Sie steht nur noch bei dir, und du kannst sie weiter bearbeiten.',
          frage:
            'Diese Reise zurückziehen?\n\nSie verschwindet aus den Listen aller Mitlesenden und steht nur noch bei dir. Gelöscht wird nichts: Nachrichten und Empfänger bleiben, und du kannst sie jederzeit wieder freigeben.',
        },
      },

      /** Das Buch und die privaten Dokumente. */
      buch: {
        titel: 'Als Buch',
        leadVor: 'Beiträge, Fotos, Karte und Punkte der Reihe nach, zum Drucken oder als PDF. ',
        leadFett: 'Antworten stehen nicht darin;',
        leadNach: ' die privaten Gespräche gibt es je Person einzeln.',
        zeitraumHinweis:
          'Beide Felder können leer bleiben, dann kommt der ganze Bericht. Der Zeitraum gilt auch für Karte, Punkte und die Dokumente darunter.',
        oeffnen: 'Buch öffnen',
        jePerson: 'Euer Briefwechsel, je Person einzeln:',
      },

      /** Die Marke „aktiv" — genau eine, und nur an einer laufenden Reise. */
      aktiv: {
        titel: 'Aktive Reise',
        gesetzt: 'Diese Reise steht bei allen Mitlesenden oben in der Liste. Die übrigen Reisen sind darunter zusammengeklappt.',
        offen: 'Markiere die Reise, auf der du gerade unterwegs bist. Sie steht dann bei allen Mitlesenden oben in der Liste, die übrigen Reisen darunter zusammengeklappt. Es kann nur eine Reise aktiv sein. Beim Abschließen oder Zurückziehen fällt die Marke weg.',
        loesen: 'Marke wieder lösen',
        setzen: 'Als aktive Reise markieren',
      },

      /** Trägt auch ein Beitrag an alle seine Marke? */
      kennzeichnung: {
        titel: 'Kennzeichnung',
        knopf: 'Beiträge an alle kennzeichnen',
        an: 'Jede Nachricht zeigt, ob sie an alle oder an einen Einzelnen geht.',
        aus: 'Nur Nachrichten an einen Einzelnen sind gekennzeichnet.',
      },

      /** Push und Hervorhebung — je Reise. */
      stumm: {
        titel: 'Benachrichtigung',
        istStumm: 'Diese Reise ist stummgeschaltet: keine Push-Nachrichten und keine Hervorhebung in der Liste. Neue Nachrichten kommen trotzdem an.',
        laut: 'Neue Nachrichten werden in der Liste hervorgehoben, und du bekommst eine Push-Nachricht. Stummschalten hält beides an, nur für diese Reise und jederzeit umkehrbar.',
        aufheben: 'Stummschaltung aufheben',
        setzen: 'Stummschalten',
      },

      /** Einladungslinks. */
      einladen: {
        titel: 'Einladen',
        mitBestaetigung:
          'Teile den Link, wie du möchtest. Wer ihn öffnet, fragt bei dir an und liest mit, sobald du zustimmst.',
        ohneBestaetigung:
          'Teile den Link, wie du möchtest. Wer ihn öffnet, legt ein Konto an und liest sofort mit.',
        einerWartet: 'einer wartet auf dich',
        wartenAufDich: (anzahl: number) => `${anzahl} warten auf dich`,
        nochNichtBenutzt: 'noch nicht benutzt',
        einmalBenutzt: 'einmal benutzt',
        malBenutzt: (anzahl: number) => `${anzahl}-mal benutzt`,
        fuerEine: 'Gilt für eine Person',
        fuerMehrere: (anzahl: number) => `Gilt für bis zu ${anzahl} Beitritte`,
        laeuftAm: ' · läuft am ',
        ab: ' ab',
        kopiert: 'Kopiert',
        kopieren: 'Link kopieren',
        deaktivieren: 'Link deaktivieren',
        deaktivierenKurz: 'Deaktivieren',
        deaktivierenFrage:
          'Diesen Link deaktivieren? Wer ihn noch nicht eingelöst hat, kommt damit nicht mehr herein. Wer schon dabei ist, bleibt dabei. Das lässt sich nicht rückgängig machen, für neue Einladungen brauchst du einen neuen Link.',
        keinLinkHinein: 'Solange die Reise nur für dich steht, führt kein Link hinein.',
        neuerLink: 'Neuen Link erzeugen',
        linkFuerEine: 'Link für eine Person',
        bestaetigenKnopf: 'Beitritt bestätigen',
        bestaetigenAn: 'Wer den Link öffnet, kommt erst herein, wenn du zustimmst. Auch mit neuem Konto.',
        bestaetigenAus: 'Wer den Link hat, liest sofort mit, auch wenn ihn jemand weitergegeben hat.',
      },

      /** Wer mitlesen möchte — und was aus einer Ablehnung wird. */
      anfragen: {
        titel: 'Möchte mitlesen',
        niemand: 'Zurzeit wartet niemand auf deine Bestätigung.',
        mitZahl: (anzahl: number) => `Möchte mitlesen (${anzahl})`,
        abgelehnteTitel: 'Abgelehnte Anfragen',
        lead: 'Diese Personen haben deinen Einladungslink geöffnet. Erst mit deiner Bestätigung lesen sie den Bericht.',
        aufnehmen: 'Aufnehmen',
        trotzdemFrage: (grund: string, name: string) => `${grund} ${name} trotzdem aufnehmen?`,
        trotzdem: 'Trotzdem aufnehmen',
        ablehnenKnopf: 'Nur für diese Reise ablehnen',
        ablehnen: 'Ablehnen',
        ablehnenFrage: (name: string) =>
          `${name} für diese Reise ablehnen? Die Person erfährt nichts davon und wartet weiter auf eine Bestätigung. Das Konto bleibt, und du kannst sie später doch aufnehmen.`,
        sperrenKnopf: 'Ablehnen und Konto sperren',
        sperren: 'Ablehnen und sperren',
        sperrenFrage: (name: string) =>
          `${name} ablehnen und das Konto sperren? Die Person wird sofort abgemeldet und kommt nirgends mehr herein, auch nicht in andere Reisen. Unter Einstellungen → Personen kannst du das zurücknehmen.`,
        abgelehntMitZahl: (anzahl: number) => `Abgelehnt (${anzahl})`,
        abgelehntLead:
          'Diese Personen warten weiter auf eine Bestätigung und erfahren nichts von der Ablehnung. Du kannst sie jederzeit doch aufnehmen.',
        dochAufnehmen: 'Doch aufnehmen',
      },

      /** Die Empfängerliste. */
      empfaenger: {
        titelMitZahl: (anzahl: number) => `Empfänger (${anzahl})`,
        lead: 'Die Empfänger sehen weder diese Liste noch die Antworten der anderen.',
        zuordnen: 'Personen zuordnen',
        legendeErreichbar: ' bekommt Benachrichtigungen',
        legendeStumm: ' stummgeschaltet',
        legendeStummOhne: ' stumm, ohne Benachrichtigungen',
        ausgestiegen: ' · ausgestiegen',
        stillgelegt: ' · stillgelegt',
        nichtsNeues: ' · bekommt nichts Neues',
        entfernen: (name: string) => `${name} entfernen`,
        entfernenFrage: (name: string) =>
          `${name} aus dieser Reise entfernen? Die Person kann den Bericht dann nicht mehr lesen. Ihre bisherigen Antworten bleiben für dich sichtbar.`,
        entfernenKnopf: 'Entfernen',
        willkommenSenden: 'Willkommen senden',
        abschneidenFrage: (name: string) =>
          `${name} nichts Neues mehr schicken? Der Bericht bleibt für die Person stehen, wie er jetzt ist. Sie merkt nichts davon. Das kannst du jederzeit rückgängig machen.`,
        abschneiden: 'Nichts Neues mehr',
        wiederBeliefern: 'Wieder beliefern',
        abschneidenKnopf: 'Nichts Neues mehr schicken',
        niemand: 'Noch niemand beigetreten.',
      },

      /** Selbst aussteigen — nur für Mitlesende. */
      verlassen: {
        titel: 'Diese Reise verlassen',
        leadVor: 'Du liest dann nicht mehr mit. Was du geschrieben hast, bleibt beim Reisenden. ',
        leadFett: 'Zurück geht es nur über eine neue Einladung',
        leadNach: ', und auch dann muss der Reisende zustimmen.',
        dieseReise: 'dieser Reise',
        frage: (titel: string) =>
          `Aus „${titel}" aussteigen? Du liest dann nicht mehr mit. Zurück kommst du nur über eine neue Einladung des Reisenden.`,
        aussteigen: 'Aussteigen',
      },
    },

    /** Der Gruß an alle, die neu zugeordnet werden. */
    willkommen: {
      titel: 'Willkommensnachricht',
      lead: 'Der Gruß an alle, die du dieser Reise neu zuordnest. Er geht als private Nachricht nur an die einzelne Person.',
      text: 'Text',
      platzhalter: 'Hallo %N, schön, dass du mitliest!',
      /** Steht noch niemand in der Runde, tut es ein Beispielname. */
      beispielname: 'Anna',
      marke: '%N',
      markeHinweis:
        ' wird durch den Namen der Person ersetzt. Fotos gehen nicht mit.',
      vorschauVor: 'So sieht sie bei ',
      vorschauNach: ' aus:',
      wirdGespeichert: 'Wird gespeichert …',
      speichern: 'Text speichern',
      alleVersendet: 'Alle versendet',
      anAlleSenden: (offene: number) => `An alle senden, die sie noch nicht haben (${offene})`,
      niemandOffen: 'Es gab niemanden, der sie noch nicht hatte.',
      verschickt: (anzahl: number) => `Verschickt: ${anzahl}.`,
    },

    /** Personen ohne Einladungslink zuordnen. */
    zuordnen: {
      titel: 'Personen zuordnen',
      schliessen: 'Zuordnung schließen',
      lead: 'Wer hier ein Häkchen bekommt, liest den Bericht sofort mit, ohne Einladungslink und ohne Bestätigung.',
      filtern: 'Namen filtern',
      laedt: 'Wird geladen …',
      niemand: 'Es gibt sonst niemanden mit einem Konto.',
      keinTreffer: 'Kein Name passt dazu.',
      alleDabei: 'Alle lesen schon mit.',
      offene: (anzahl: number) =>
        `${anzahl} ${anzahl === 1 ? 'Person kann' : 'Personen können'} noch dazukommen.`,
      zuordnenLabel: (name: string) => `${name} zuordnen`,
      /** Was neben dem Namen steht — vom Server entschieden, hier nur benannt. */
      stand: {
        dabei: 'liest mit',
        wartet: 'wartet auf Bestätigung',
        ausgestiegen: 'ausgestiegen',
        offen: '',
      },
    },

    /**
     * Den Inhalt einer Reise wegwerfen und die Reise behalten.
     *
     * Zwei Schritte, und der erste ist keine Zierde: erst steht da, was genau
     * verschwindet, dann muss ein Code abgetippt werden.
     */
    zuruecksetzen: {
      titel: 'Chat zurücksetzen',
      was: 'Löscht den ganzen Inhalt dieser Reise: Nachrichten mit Fotos und Videos, Reaktionen, Lesestände, die Route und alle Punkte auf der Karte. Die Reise selbst bleibt, mit Empfängern, Einladungen, Einstellungen, Symbolbild und Chat-Hintergrund.',
      wofuerVor: 'Gedacht für die Probe zu zweit, bevor die eigentlichen Empfänger dazukommen. ',
      wofuerFett: 'Das lässt sich nicht rückgängig machen.',
      knopf: 'Zurücksetzen …',
      wirdGezaehlt: 'Wird gezählt …',
      uebersichtFehlt: 'Die Übersicht lässt sich gerade nicht laden.',
      verschwindet: 'Das verschwindet:',
      nachricht: 'Nachricht',
      nachrichten: 'Nachrichten',
      medium: 'Foto oder Video',
      medien: 'Fotos und Videos',
      datei: 'Datei',
      dateien: 'Dateien',
      gpx: 'GPX-Datei',
      gpxMehrere: 'GPX-Dateien',
      punkt: 'Punkt auf der Karte',
      punkte: 'Punkte auf der Karte',
      punktKurz: 'Punkt',
      punkteKurz: 'Punkte',
      mitGroesse: (was: string) => ` · ${was}`,
      leserEins: 'Person liest',
      leserViele: 'Personen lesen',
      leserWarnung: ' diese Reise mit. Auch ihre Antworten verschwinden.',
      abtippenVor: 'Zum Bestätigen abtippen: ',
      abtippenLabel: (code: string) => `Code ${code} abtippen`,
      ohneNetz: 'Ohne Netz geht das gerade nicht.',
      laeuft: 'Wird zurückgesetzt …',
      endgueltig: 'Endgültig zurücksetzen',
      fertig: (titel: string, nachrichten: string, dateien: string, gpx: string, punkte: string) =>
        `„${titel}" ist zurückgesetzt: ${nachrichten}, ${dateien}, ${gpx} und ${punkte} sind weg.`,
      anzahl: (anzahl: number, eins: string, viele: string) =>
        `${anzahl} ${anzahl === 1 ? eins : viele}`,
    },

    /** Die Reise für immer löschen (#15) -- der Schritt nach dem Zurücksetzen. */
    loeschen: {
      titel: 'Reise löschen',
      was: 'Löscht die ganze Reise: alle Nachrichten mit Fotos und Videos, die Route, die Punkte auf der Karte, Symbolbild und Chat-Hintergrund, alle Empfänger und Einladungen. Auch die Mitlesenden sehen die Reise danach nicht mehr.',
      sichernVor: 'Wenn du den Bericht behalten willst, erzeuge vorher das Buch oder lade die Medien als ZIP herunter. ',
      sichernFett: 'Das Löschen lässt sich nicht rückgängig machen.',
      knopf: 'Reise löschen …',
      dieReise: (titel: string) => `Die Reise „${titel}" mit allen Empfängern und Einladungen`,
      mitBildern: ', Symbolbild und Chat-Hintergrund eingeschlossen',
      buch: 'gebautes Buch oder ZIP auf dem Server',
      buecher: 'gebaute Bücher oder ZIPs auf dem Server',
      leserWarnung: ' diese Reise mit. Sie verlieren den ganzen Bericht.',
      laeuft: 'Wird gelöscht …',
      endgueltig: (titel: string) => `„${titel}" endgültig löschen`,
      nurPrivat: 'Löschen geht nur, wenn die Reise nur für dich steht. Stell sie zuerst um:',
      zumZustand: 'Zum Zustand der Reise',
    },

    /** Die Farbe der Sprechblasen — einmal für alle, einmal nur für dich. */
    blasen: {
      titel: 'Farbe der Sprechblasen',
      leadReise: 'Gilt für alle, die diese Reise mitlesen. Die Vorgabe kam beim Anlegen aus den Einstellungen.',
      leadEigen: 'Nur für dich. Die anderen sehen weiter die Farbe des Reisenden.',
      wieDerReisende: 'Wie der Reisende',
    },
  },

  /** Die Reise als Buch — und das private Dokument je Person. */
  /** Die Wetterkarte im Chat (0.31.0, seit 0.32.0 mit Symbolen statt Wörtern). */
  tageskarte: {
    titel: (tag: string) => `Am ${tag} gefahren`,
    strecke: 'Strecke',
    fahrzeit: 'Fahrzeit',
    hoehenmeter: 'Höhenmeter',
    hoechsterPunkt: 'Höchster Punkt',
    start: 'Start',
    ziel: 'Ziel',
    highlights: 'Highlights:',
    trenner: ' · ',
    km: (km: number) => `${km.toLocaleString('de-DE', { maximumFractionDigits: km < 100 ? 1 : 0, minimumFractionDigits: km < 100 ? 1 : 0 })} km`,
    dauer: (sekunden: number) => {
      const minuten = Math.round(sekunden / 60)
      return minuten < 60 ? `${minuten} Min.` : `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, '0')} Std.`
    },
    bergauf: (m: number) => `↑ ${m.toLocaleString('de-DE')} m`,
    bergab: (m: number) => `↓ ${m.toLocaleString('de-DE')} m`,
    meter: (m: number) => `${m.toLocaleString('de-DE')} m`,
    ortUmZeit: (zeit: string, ort: string) => `${zeit} · ${ort}`,
  },

  wetterkarte: {
    grad: (grad: number) => `${grad}°`,
    trenner: ' · ',
    prozent: (prozent: number) => `${prozent} %`,
    /** Die Wörter zu den drei Kacheln (seit 0.37.0: Wind, Luftfeuchte mit Gefühlt, Mond). */
    gefuehlt: 'Gefühlt',
    hoechst: 'Höchstwert',
    tiefst: 'Tiefstwert',
    wind: 'Wind',
    luftfeuchte: 'Luftfeuchte',
    richtungWert: (grad: number, strich: string) => `${grad}° ${strich}`,
    kmh: (kmh: number) => `${kmh} km/h`,
    boeenWert: (kmh: string) => `Böen ${kmh}`,
    /** Auf- und Untergang des Mondes in einer Zeile: „↑ 13:05 ↓ 22:40". */
    mondZeiten: (auf: string | null, unter: string | null) => [auf === null ? null : `↑ ${auf}`, unter === null ? null : `↓ ${unter}`].filter((s) => s !== null).join(' '),
    himmel: { n: 'N', o: 'O', s: 'S', w: 'W' },
    sonnenaufgang: 'Sonnenaufgang',
    sonnenuntergang: 'Sonnenuntergang',
    heute: 'Heute',
    stunden: 'Die nächsten Stunden',
    jetzt: 'Jetzt',
    stunde: (stunde: number) => `${String(stunde).padStart(2, '0')} Uhr`,
    standort: 'Am Standort des Geräts beim Antippen',
    stundeZeile: (wann: string, lage: string | null, grad: number | null) =>
      `${wann}${lage === null ? '' : `, ${lage}`}${grad === null ? '' : `, ${grad}°`}`,
    regenchance: (prozent: number) => `Regenwahrscheinlichkeit ${prozent} %`,
    tagZeile: (tag: string, lage: string | null, tief: number | null, hoch: number | null) =>
      `${tag}${lage === null ? '' : `, ${lage}`}${tief === null ? '' : `, tiefstens ${tief}°`}${hoch === null ? '' : `, höchstens ${hoch}°`}`,
    mondphasen: {
      new: 'Neumond',
      waxingCrescent: 'zunehmende Sichel',
      firstQuarter: 'zunehmender Halbmond',
      waxingGibbous: 'zunehmender Mond',
      full: 'Vollmond',
      waningGibbous: 'abnehmender Mond',
      thirdQuarter: 'abnehmender Halbmond',
      waningCrescent: 'abnehmende Sichel',
    } as Record<string, string>,
  },

  buch: {
    zurueck: 'Zurück zur Reise',
    zusammenstellen: (seiten: number) => `Wird zusammengestellt … (${seiten} Seiten)`,
    umfang: (beitraege: number, punkte: number) => `${beitraege} Beiträge, ${punkte} Punkte`,
    nachrichten: (anzahl: number) => `${anzahl} Nachrichten`,
    nochNichtFertig: 'Noch nicht fertig …',
    nichtBereit: 'Nicht bereit',
    /** Das Buch als Datei vom Server (#14): zwei Knöpfe statt des Druckdialogs. */
    alsPdf: 'Als PDF',
    alsEpub: 'Als EPUB',
    /** Die drei Schritte hinter dem Knopf, damit man sieht, wo es hängt (0.30.9). */
    exportKarten: 'Karten werden abgemalt …',
    exportServer: 'Der Server baut …',
    exportHolen: 'Datei wird geholt …',
    exportKeineAntwort: 'Der Server hat nach sechs Minuten nicht geantwortet. Versuch es mit einem kürzeren Zeitraum. Unter Einstellungen › Diagnose steht, wie weit es kam.',
    /** Der Link unter den Knöpfen, wenn die Datei gebaut ist. */
    dateiOeffnen: (name: string) => `${name} öffnen`,
    /** Die Medien als ZIP (#75): ein Knopf, ein Link, ein Satz zu den Ortsnamen. */
    medienZip: 'Medien als ZIP',
    medienPackt: 'Der Server packt …',
    medienOeffnen: (name: string, dateien: number, groesse: string) => `${name} öffnen (${dateien === 1 ? '1 Datei' : `${dateien} Dateien`}, ${groesse})`,
    medienOrteApple: 'Fotos, Videos und Sprachnachrichten in drei Ordnern, benannt nach Datum, Ort und Nummer. Den Ort holt der Server einmal je Foto mit Ortsangabe bei Apple Maps.',
    medienOrteOhne: 'Fotos, Videos und Sprachnachrichten in drei Ordnern, benannt nach Datum, Ort und Nummer. Ohne Apple Maps heißt der Ort „unbekannt".',
    appHinweis: 'In der App öffnet sich dafür Safari. Dort lässt sich die Datei sichern.',
    /** Steht in der Fußzeile und auf dem Deckel, wenn der Titel fehlt. */
    reise: 'Reise',
    ausschnitt: (zeitraum: string) => `Ausschnitt: ${zeitraum}`,
    route: 'Die Route',
    bericht: 'Der Bericht',
    keinBeitragImZeitraum: 'In diesem Zeitraum steht kein Beitrag.',
    keinBeitrag: 'In diesem Bericht steht noch kein Beitrag.',
    punkte: 'Die Punkte auf der Karte',
    kartenausschnitte: (vermerk: string) => `Kartenausschnitte: ${vermerk}`,
    empfaenger: 'Empfänger',
    stand: (datum: string) => `Stand: ${datum}`,

    /** Ein Medium auf Papier — es steht als Angabe da, nicht als Abspieler. */
    sprachnachricht: (sekunden: number, groesse: string) =>
      `Sprachnachricht · ${sekunden} s · ${groesse}`,
    ohneStandbild: 'Ohne Standbild',
    video: (minuten: number, sekunden: string) => `Video · ${minuten}:${sekunden}`,
    videoHoehe: (hoehe: number) => ` · ${hoehe}p`,
    videoCodec: (codec: string) => ` · ${codec}`,
    grad: (grad: number) => `${grad} °C`,
    wetterWort: (wort: string) => `, ${wort}`,
    /** Die Nennung ist Pflicht, wo Wetter steht. */
    wetterquelle: ' (Apple Weather)',

    /**
     * Eine Abstimmung auf Papier.
     *
     * **Gedruckt wird die Sicht eines Lesers, obwohl der Reisende druckt.**
     * Der Server schickt ihm Namen und absolute Zahlen mit — im Buch stehen
     * sie trotzdem nicht: das Buch wird weitergereicht, und „Anna: Ja" verriete
     * jedem, der es in die Hand bekommt, wie eine Einzelne gestimmt hat. Das
     * ist dieselbe Grenze, an der auch die Antworten draußen bleiben, und
     * dieselbe, die schon bei den Reaktionen gilt: dort stehen die Zeichen und
     * nicht, wer sie gesetzt hat.
     *
     * **Auch keine Stimmenzahl.** Aus „sieben Stimmen" ließe sich die Größe
     * des Kreises zurückrechnen; aus „43 Prozent" nicht. Der Server begründet
     * das an derselben Stelle (`Abstimmung::ergebnis()`).
     *
     * **Die Frage steht hier nicht**, und das ist kein Versehen: der Server
     * schreibt sie als Text des Beitrags („Abstimmung: Wo fahren wir hin?"),
     * damit sie in der Chatliste und in der Benachrichtigung steht. Im Buch
     * ist sie damit schon gedruckt — sie ein zweites Mal zu setzen hieße, sie
     * doppelt zu lesen.
     */
    abstimmung: {
      anteil: (prozent: number) => `${prozent} %`,
      geschlossen: 'geschlossen',
      mehrfach: 'Mehrfachauswahl',
      /** Steht da, wo es (noch) kein Ergebnis gibt — statt einer leeren Stelle. */
      ohneErgebnis: 'Noch kein Ergebnis.',
      trenner: ' · ',
    },

    /** Das private Dokument — je Person eines, und es sagt das auf Seite eins. */
    privat: {
      diesePerson: 'dieser Person',
      dateiname: (reise: string, wer: string) => `${reise}, privat mit ${wer}`,
      fuss: (reise: string, wer: string) => `${reise}, privat mit ${wer}`,
      deckel: (wer: string) => `Privates Gespräch mit ${wer}`,
      warnungVor: 'Dieses Dokument ist ',
      warnungNicht: 'nicht',
      warnungNach: (wer: string) =>
        ` der Reisebericht. Es enthält den Briefwechsel zwischen dir und ${wer}: die Antworten und deine privaten Nachrichten. Niemand sonst hat sie je gesehen. Zum Weitergeben nimm das Buch, nicht dieses Dokument.`,
      briefwechsel: 'Der Briefwechsel',
      leerImZeitraum: 'In diesem Zeitraum steht zwischen euch nichts.',
      leer: 'Zwischen euch steht noch nichts.',
      du: 'Du',
      zitat: (text: string) => `„${text}"`,
    },
  },

  /** Anmelden, Konto anlegen, Passwort vergessen — und der Einladungslink. */
  anmeldung: {
    anmelden: 'Anmelden',
    email: 'E-Mail-Adresse',
    passwort: 'Passwort',
    passwortVergessen: 'Passwort vergessen?',
    /** Anmelden mit Passkey (#21): der Knopf unter dem Formular. */
    oder: 'oder',
    mitPasskey: 'Mit Passkey anmelden',
    passkeyAbgebrochen: 'Abgebrochen. Du kannst dich mit E-Mail-Adresse und Passwort anmelden.',
    keinKontoVor: 'Du hast einen Einladungslink? Öffne ihn, darüber legst du dein Konto an. ',
    registrierung: 'Registrierung',

    kontoAnlegen: 'Konto anlegen',
    kontoLead: 'Der Name steht später über deinen Nachrichten.',
    name: 'Name',
    einladungscode: 'Einladungscode',
    codeBeispiel: 'z. B. K7M2QRXD',
    schonKontoVor: 'Schon ein Konto? ',

    vergessenTitel: 'Passwort vergessen',
    vergessenLead: 'Trag deine E-Mail-Adresse ein. Wir schicken dir einen Link, über den du ein neues Passwort vergibst.',
    linkAnfordern: 'Link anfordern',
    postfachTitel: 'Sieh in dein Postfach',
    postfachVor: 'Wenn es zu ',
    postfachNach: ' ein Konto gibt, ist eine Nachricht mit einem Link unterwegs. Er gilt eine Stunde und lässt sich einmal verwenden.',
    postfachSpam: 'Nichts angekommen? Sieh im Spam-Ordner nach, und prüfe die Schreibweise der Adresse.',
    zurueckZurAnmeldung: 'Zurück zur Anmeldung',

    neuesPasswort: 'Neues Passwort',
    neuesPasswortLead: 'Vergib ein neues Passwort. Danach bist du gleich angemeldet. Alle anderen Geräte werden abgemeldet.',
    nochEinmal: 'Noch einmal',
    stimmtNichtUeberein: 'Die beiden Eingaben stimmen nicht überein.',
    passwortSpeichern: 'Passwort speichern',
    abgelaufenVor: 'Link abgelaufen? ',
    neuenAnfordern: 'Neuen anfordern',

    /** Der Einladungslink — vier Zustände, und sie sind auseinanderzuhalten. */
    einladung: {
      dieseReise: 'Diese Reise',
      schonDabei: 'Du liest hier schon mit. Es gibt nichts mehr zu bestätigen.',
      zurReise: 'Zur Reise',
      wirdGeprueft: 'Einladung wird geprüft …',
      ungueltig: 'Einladung ungültig',
      weiterZurAnwendung: 'Weiter zur Anwendung',
      angefragt: (wer: string) =>
        `Deine Anfrage ist bei ${wer}. Sobald sie bestätigt ist, steht der Reisebericht in deiner Liste.`,
      laedtEin: (wer: string) => `${wer} lädt dich ein, den Reisebericht mitzulesen.`,
      nurDerAbsender: (wer: string) =>
        `Du liest alle Beiträge. Deine Antworten sieht nur ${wer}, die anderen Empfänger nicht.`,
      bestaetigtJeden: (wer: string) =>
        `${wer} bestätigt jeden Beitritt. Sobald das passiert ist, liest du mit.`,
      beitrittAnfragen: 'Beitritt anfragen',
      beitreten: 'Beitreten',
      angemeldetAls: (name: string) => `Angemeldet als ${name}`,
      kontoUndBeitreten: 'Konto anlegen und beitreten',
      habeKonto: 'Ich habe schon ein Konto',
    },
  },

  /** Der Rahmen: Startseite, Wartetafel, was zwischen den Seiten steht. */
  rahmen: {
    karteLaedt: 'Karte wird geladen …',
    /** Die Startseite auf breiten Fenstern — links die Liste, hier nichts. */
    start: {
      waehleMitAnlegen: 'Wähle links eine Reise aus oder lege eine neue an.',
      waehle: 'Wähle links eine Reise aus.',
      alsAutor: 'Was du schreibst, lesen alle Empfänger. Was sie antworten, liest nur du.',
      alsLeser: 'Deine Antworten liest nur der Reisende, die anderen Empfänger nicht.',
    },
    /**
     * Was jemand sieht, der noch nirgends dabei ist.
     *
     * **Wer abgelehnt wurde, sieht dieselbe Tafel.** Das ist keine
     * Nachlässigkeit, sondern dieselbe Entscheidung wie überall: eine
     * mitgeteilte Ablehnung ist eine Aufforderung nachzufragen.
     */
    warten: {
      nichts: 'Noch nichts freigegeben',
      angefragt: (wer: string) =>
        `Deine Anfrage ist bei ${wer}. Sobald sie bestätigt ist, steht der Reisebericht hier.`,
      eingeladenWerden: 'Sobald dich jemand zu einem Reisebericht einlädt, geht es hier weiter.',
      angemeldetAls: (name: string, email: string) => `Angemeldet als ${name} (${email})`,
      abmelden: 'Abmelden',
    },
  },

  /** Die kleinen Bauteile, die überall mitlaufen. */
  bausteine: {
    /** Der Deckel über einem langen Text. */
    langtext: {
      mehr: 'Mehr anzeigen',
      weniger: 'Weniger',
    },
    /** Der Streifen, wenn nichts durchgeht. */
    netz: {
      kein: 'Kein Netz',
      wartend: (anzahl: number) =>
        `: ${anzahl} ${anzahl === 1 ? 'Eintrag wartet' : 'Einträge warten'} und ${anzahl === 1 ? 'geht' : 'gehen'} später von selbst raus.`,
      leer: ': was du schreibst, geht später von selbst raus.',
    },
    /** Wie viel noch geht — erst, wenn es knapp wird. */
    restzeichen: {
      zuViel: (zuViel: number) => `${zuViel} Zeichen zu viel.`,
      rest: (rest: number, grenze: number) => `Noch ${rest} von ${grenze} Zeichen.`,
      zusatz: (satz: string) => ` · ${satz}`,
    },
    /**
     * Erreichbarkeit und Ruhe eines Mitlesenden — als ein Zeichen.
     *
     * Vier Zustände, drei Sätze: „kein Gerät angemeldet" bekommt bewusst
     * keinen, weil es kein Zustand ist, sondern der Normalfall.
     */
    erreichbarkeit: {
      stummMitPush: 'Stummgeschaltet. Benachrichtigungen sind eingerichtet, kommen aber nicht an.',
      stummOhnePush: 'Stummgeschaltet, und keine Benachrichtigungen eingerichtet.',
      erreichbar: 'Bekommt Benachrichtigungen.',
    },
    /** Den Ausschnitt eines Symbolbildes wählen. */
    ausschnitt: {
      ziehen: 'Zieh das Bild an die Stelle, die zu sehen sein soll.',
      ganzesBild: ' Ganz nach links steht das ganze Bild auf der Hintergrundfarbe.',
      groesse: (zoom: number) => `Größe: ${zoom} %`,
      uebernehmen: 'Übernehmen',
      zuruecksetzen: 'Zurücksetzen',
    },
  },
} as const
